import { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import ComposeWindow from "./ComposeWindow";
import RotationTestingPanel from "./RotationTestingPanel";
import { interpolateMergeTags, applyConditionalBlocks } from "./mergeTags";
// Phase 4b (Workspace/Mail Merge modal/context-menu unification, see
// linear-brewing-toast.md) — same shared shells already adopted by
// OutlookWorkspace.js. Each dialog below keeps its own fields/content/logic
// untouched; only the surrounding backdrop/card/menu box is now shared.
// (useModalA11y itself is no longer imported here — every dialog that used
// to call it directly now gets focus-trap/Escape internally from Modal/
// ContextMenu instead.)
import { Modal } from "./ui/Modal";
import { ContextMenu, ContextMenuItem, ContextMenuDivider } from "./ui/ContextMenu";
import { RIcon } from "./RibbonIcons";
import { RBtn as RBtnShared, RGroup as RGroupShared, RibbonTabButton, RibbonDropdownButton, StatusChip } from "./ribbon/RibbonPrimitives";
import { loadTemplates, saveTemplates, fetchTemplateRepo, createTemplateRepo, updateTemplateRepo, deleteTemplateRepo, markTemplateUsed, TEMPLATE_CATEGORIES } from "./emailTemplates";
import MergePreviewBar from "./MergePreviewBar";
import MailMergeFullPreview from "./MailMergeFullPreview";
import "react-quill/dist/quill.snow.css";
import RichTextEditor from "./richtext/RichTextEditor";
import WordRibbon from "./richtext/WordRibbon";
import FindReplaceDialog from "./richtext/FindReplaceDialog";
import { insertHorizontalLine } from "./richtext/wordFormats";
import { useVoiceDraft } from "./richtext/voiceDictation";
import { WORKSPACE_THEME, WORKSPACE_RADIUS } from "./workspaceTheme";

// Mail Merge's own starter templates — unchanged content, just pulled out to
// a named constant so it can be passed as the one-time seed to the shared,
// localStorage-backed template store (see emailTemplates.js) instead of
// being a plain useState initial value nobody else could ever see.
const DEFAULT_TEMPLATES = [
  {id:1,name:"Cold Outreach - Tech",subject:"Quick question, {{FirstName}}",body:"<p>Hi {{FirstName}},</p><p>I came across {{Company}} and was impressed by your work. I wanted to reach out about...</p>"},
  {id:2,name:"Follow-Up Template",subject:"Re: Quick question, {{FirstName}}",body:"<p>Hi {{FirstName}},</p><p>Just following up on my previous email. I'd love to connect...</p>"},
];
// Intentionally still a no-op. Paste cleanup now happens globally, once,
// inside RichTextEditor.js (client/src/richtext/pasteClean.js) — every
// surface built on RichTextEditor, including this one, gets Word/Outlook/
// Google-Docs cruft stripped on every Ctrl+V automatically, without each
// surface needing its own matcher. Left in place (rather than removed) so
// the existing call sites below don't need to change.
const registerCleanPaste = (quill) => {};

// The "divider" blot insertDivider() (below) inserts is now registered once,
// globally, by wordFormats.js's registerQuillFormats — alongside the same
// "Horizontal Line" ribbon button and "---"+Enter shorthand every other
// RichTextEditor surface (Workspace, Sequences) also gets. This file used to
// register its own local copy of that exact blot under the same "divider"
// name; removed in favor of the shared one so the two registrations aren't
// silently overwriting each other. insertDivider() below is unchanged — it
// only ever referenced the format by name ("divider"), never this file's own
// registration, so it keeps working exactly as before. Old Mail Merge
// content already containing a class="mm-divider" <hr> (from before this
// change) keeps rendering fine too — the CSS rule for it, further down this
// file, hasn't moved.

// Was a local, hand-copied palette (21 keys). Every value below was
// byte-for-byte identical to WORKSPACE_THEME's, so this is now just an
// alias — same rendered output, one shared source of truth with
// OutlookWorkspace.js and FileMenu.js instead of a fourth independent copy.
// See workspaceTheme.js's own header comment for the dedup history.
const C = WORKSPACE_THEME;
const WR = WORKSPACE_RADIUS;

const MERGE_TAGS = [
  "{{FirstName}}","{{LastName}}","{{Title}}","{{Company}}",
  "{{CompanyNameForEmails}}","{{Email}}","{{SenderName}}","{{SenderCompany}}"
];
// Friendly labels for the Insert Merge Field dropdown — same 8 tags
// mergeTags.js's BUILTIN_RESOLVERS already supports, just human-readable
// instead of the raw {{TagName}} syntax, matching how Word's own Insert
// Merge Field menu reads (e.g. "Job Title" not "{{Title}}").
const MERGE_TAG_LABELS = {
  "{{FirstName}}":"First Name","{{LastName}}":"Last Name","{{Title}}":"Job Title",
  "{{Company}}":"Company","{{CompanyNameForEmails}}":"Company (email-safe)",
  "{{Email}}":"Email","{{SenderName}}":"Sender Name","{{SenderCompany}}":"Sender Company",
};
// Recipient object keys that are already covered by a standard field above,
// or are internal bookkeeping (id, suppressed, tags, etc.) rather than real
// personalization data — excluded from the dropdown's dynamic "Other
// Fields" section so it only ever lists genuinely-extra data (e.g. phone,
// linkedin — real Contact fields that "Use Existing List" recipients carry
// but CSV imports, which only keep 6 mapped columns, never do).
const RESERVED_RECIPIENT_KEYS = new Set([
  "id","_id","name","first","last","firstName","lastName","email","company","title",
  "leadStatus","status","tags","notes","source","contactFile","suppressed",
  "createdAt","updatedAt","__v","tenantId","userId","customFields",
]);

const FOLLOW_UPS = [
  {id:"initial",label:"Initial Email",day:"Day 1",color:C.accent},
  {id:"fu1",label:"Follow-Up 1",day:"Day 3",color:"#06b6d4"},
  {id:"fu2",label:"Follow-Up 2",day:"Day 7",color:C.amber},
  {id:"fu3",label:"Follow-Up 3",day:"Day 14",color:C.purple},
  {id:"fu4",label:"Follow-Up 4",day:"Day 21",color:C.green},
  {id:"fu5",label:"Follow-Up 5",day:"Day 30",color:C.gold},
  {id:"fu6",label:"Follow-Up 6",day:"Day 45",color:C.red},
];




// RBtn and RGroup now live in ./ribbon/RibbonPrimitives, shared with
// OutlookWorkspace.js — see that file's header comment for why they were
// extracted. These two thin wrappers pass `dense` automatically so every
// existing <RBtn>/<RGroup> call site below keeps this file's own original
// (slightly tighter than Workspace's) button/group metrics without having
// to change — only the icon-color-token source moved, not the sizing. One
// small, deliberate visual fix came along with the extraction: this file's
// ribbon buttons/group dividers were hardcoded to old Microsoft Fluent hex
// values (independent of whatever palette workspaceTheme.js is set to) —
// they now correctly read C.ribbonHover/C.ribbonActive/C.ribbonBorder like
// Workspace's ribbon always has, so Mail Merge's ribbon tracks palette
// changes instead of being silently stuck on Outlook-blue.
function RBtn(props){ return <RBtnShared dense {...props}/>; }
function RGroup(props){ return <RGroupShared dense {...props}/>; }

function Badge({children,color=C.accent}){
  return <span style={{background:color+"22",color,fontSize:10,padding:"1px 7px",borderRadius:3,fontWeight:600,whiteSpace:"nowrap",fontFamily:"monospace"}}>{children}</span>;
}

// ─── Stage rail (redesign plan, Phase 3) ────────────────────────────────────
//
// The plan calls for a persistent left-rail tracker across Select Contacts →
// Map Data → Create Message → AI Personalization → Preview → Validate →
// Send → Monitor, while explicitly keeping this screen's single-screen
// "live document" feel — i.e. NOT a step wizard that gates or replaces any
// existing control. This is purely an additive, informational strip: it
// reads existing state to show real progress (nothing here is a fake/
// simulated status) and, on click, focuses the relevant existing control
// rather than duplicating its logic — same "zero duplicated logic" approach
// already used for the command palette and Find & Replace.
//
// Status per stage is genuine, not decorative:
//  - Select Contacts / Map Data: recipients.length>0 (import already forces
//    the real field-mapping grid before recipients ever land in state, so
//    "recipients loaded" IS "data mapped").
//  - Create Message: any follow-up step has drafted subject+body content.
//  - AI Personalization: aiPersonalizationUsed is set the one place AI Draft
//    actually succeeds (aiGenerateStep) — not inferred/guessed.
//  - Preview: hasPreviewed is set the first time previewMode is switched on,
//    from anywhere in the UI.
//  - Validate: emailsValidated is set only when "Validate Emails" runs AND
//    finds zero invalid addresses — a real pass, not just "was clicked".
//  - Send: a run exists (mmRun) or mergeStats already shows sent mail.
//  - Monitor: current while that run is actively sending/paused; done once
//    it completes.
const STAGE_DEFS = [
  {id:"contacts",label:"Select Contacts",icon:"\u{1F465}"},
  {id:"mapdata",label:"Map Data",icon:"\u{1F517}"},
  {id:"message",label:"Create Message",icon:"✍️"},
  {id:"ai",label:"AI Personalization",icon:"✨"},
  {id:"preview",label:"Preview",icon:"\u{1F441}️"},
  {id:"validate",label:"Validate",icon:"✅"},
  {id:"send",label:"Send",icon:"\u{1F4E4}"},
  {id:"monitor",label:"Monitor",icon:"\u{1F4CA}"},
];

// `collapsed`/`onToggleCollapse` — the rail can be hidden down to a slim
// 28px sliver (icons/numbers only, via title-tooltips) rather than fully
// removed, so there's always a one-click way back in without hunting for a
// setting. Preference persists to localStorage the same way Workspace's own
// pane layout already does (see loadPaneLayout/PANE_LAYOUT_KEY in
// OutlookWorkspace.js) — collapse it once, it stays collapsed next time you
// open Mail Merge.
function MailMergeStageRail({stages,onStageClick,collapsed,onToggleCollapse}){
  if(collapsed){
    return(
      <div className="hide-on-mobile" style={{width:28,background:"#fff",borderRight:"1px solid #d1cec9",flexShrink:0,overflowY:"auto",display:"flex",flexDirection:"column",alignItems:"center",padding:"12px 0",gap:6}}>
        <button onClick={onToggleCollapse} title="Show stage tracker"
          style={{width:18,height:18,border:"1px solid #d1cec9",borderRadius:3,background:"#fff",cursor:"pointer",fontSize:9,color:C.textSub,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginBottom:4}}>
          &#9656;
        </button>
        {STAGE_DEFS.map((def,i)=>{
          const status=stages[def.id]||"upcoming";
          const isDone=status==="done", isCurrent=status==="current";
          const dotColor=isDone?C.green:isCurrent?C.accent:"#c8c6c4";
          return(
            <button key={def.id} onClick={()=>onStageClick(def.id)}
              title={def.label+" — "+(isDone?"done":isCurrent?"in progress":"not started yet")}
              style={{width:18,height:18,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:isDone?C.green:isCurrent?C.accentL:"#fff",border:"2px solid "+dotColor,fontSize:9,color:isDone?"#fff":dotColor,cursor:"pointer",padding:0}}>
              {isDone?"✓":i+1}
            </button>
          );
        })}
      </div>
    );
  }
  return(
    <div className="hide-on-mobile" style={{width:132,background:"#fff",borderRight:"1px solid #d1cec9",flexShrink:0,overflowY:"auto",padding:"8px 0 12px"}}>
      <div style={{display:"flex",justifyContent:"flex-end",padding:"0 8px 6px"}}>
        <button onClick={onToggleCollapse} title="Hide stage tracker"
          style={{width:18,height:18,border:"1px solid #d1cec9",borderRadius:3,background:"#fff",cursor:"pointer",fontSize:9,color:C.textSub,display:"flex",alignItems:"center",justifyContent:"center"}}>
          &#9666;
        </button>
      </div>
      {STAGE_DEFS.map((def,i)=>{
        const status=stages[def.id]||"upcoming";
        const isDone=status==="done", isCurrent=status==="current";
        const dotColor=isDone?C.green:isCurrent?C.accent:"#c8c6c4";
        return(
          <div key={def.id} style={{display:"flex",flexDirection:"column"}}>
            <button onClick={()=>onStageClick(def.id)}
              title={def.label+" — "+(isDone?"done":isCurrent?"in progress":"not started yet")}
              style={{display:"flex",alignItems:"center",gap:8,width:"100%",background:"none",border:"none",cursor:"pointer",padding:"5px 12px",textAlign:"left"}}
              onMouseEnter={e=>e.currentTarget.style.background="#f3f2f1"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{width:18,height:18,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:isDone?C.green:isCurrent?C.accentL:"#fff",border:"2px solid "+dotColor,fontSize:10,color:isDone?"#fff":dotColor}}>
                {isDone?"✓":i+1}
              </span>
              <span style={{fontSize:11,fontWeight:isCurrent?600:400,color:isCurrent?C.accent:isDone?C.text:C.textMuted,lineHeight:1.25}}>{def.label}</span>
            </button>
            {i<STAGE_DEFS.length-1&&(
              <div style={{width:2,height:10,background:isDone?C.green:"#e1dfdd",marginLeft:20}}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MailMergeEmailEditor({isMobile=false,closing=false,mergeTags=[],recipients=[],settings={},senders=[],contactFiles=[],fromEmail="",setFromEmail=()=>{},selectedFile="",loadFile=()=>{},importCSV=()=>{},manualEmails="",setManualEmails=()=>{},addManualRecipients=()=>{},showManualEntry=false,setShowManualEntry=()=>{},openComposeWindow=()=>{},onClose,addToast}){
  const [tabs,setTabs]=useState([{id:1,subject:"",body:"",title:"Mail Merge Email 1"}]);
  const [activeTab,setActiveTab]=useState(1);
  const [nextId,setNextId]=useState(2);
  const [ribbonTab,setRibbonTab]=useState("mailings");
  const [preview,setPreview]=useState(false);
  // Used to be hardcoded to recipients[0] — every recipient except the
  // first was unreachable in preview here, even though this component
  // already receives the full `recipients` array as a prop (the main
  // MailMerge component's own Live Preview navigator, a few hundred lines
  // away, already paginates the exact same kind of list). previewIdx below
  // was declared but never actually read anywhere — now it drives real
  // navigation via the shared MergePreviewBar (see the status bar below).
  const [previewIdx,setPreviewIdx]=useState(0);
  const previewContact=recipients[Math.min(previewIdx,Math.max(0,recipients.length-1))];
  const [sending,setSending]=useState(false);
  const quillRefs=useRef({});
  const subjectRefs=useRef({});
  const lastFocRef=useRef("body");
  const [formatMarksOn,setFormatMarksOn]=useState(false);
  const [showFindReplace,setShowFindReplace]=useState(false);
  const getQuill=()=>quillRefs.current[activeTab];

  const cur=tabs.find(t=>t.id===activeTab)||tabs[0];
  const upd=(changes)=>setTabs(p=>p.map(t=>t.id===activeTab?{...t,...changes}:t));

  const addTab=()=>{
    const id=nextId;setNextId(id+1);
    setTabs(p=>[...p,{id,subject:"",body:"",title:"Mail Merge Email "+nextId}]);
    setActiveTab(id);
  };

  const closeTab=(id,e)=>{
    e.stopPropagation();
    if(tabs.length===1){onClose();return;}
    const rest=tabs.filter(t=>t.id!==id);
    setTabs(rest);
    if(activeTab===id)setActiveTab(rest[rest.length-1].id);
  };

  const insertField=(f)=>{
    if(lastFocRef.current==="subject"){
      const inp=subjectRefs.current[activeTab];
      if(inp){
        const s=inp.selectionStart||0,e2=inp.selectionEnd||0;
        const curVal=tabs.find(t=>t.id===activeTab)?.subject||"";
        const newVal=curVal.slice(0,s)+f+curVal.slice(e2);
        upd({subject:newVal,title:newVal||("Mail Merge Email "+activeTab)});
        setTimeout(()=>{inp.focus();inp.setSelectionRange(s+f.length,s+f.length);},10);
      }
    }else{
      const q=quillRefs.current[activeTab];
      if(!q)return;
      const sel=q.getSelection(true);
      const idx=sel?sel.index:q.getLength();
      q.insertText(idx,f,{color:"#0078D4"});
      q.setSelection(idx+f.length);
    }
  };
  // Inserts a thin horizontal divider line at the cursor (body editor only).
  // Shares its implementation (insertHorizontalLine, in wordFormats.js) with
  // WordRibbon.js's "Horizontal Line" ribbon button and the app-wide
  // "---"+Enter shorthand, so this dropdown entry, the ribbon button, and
  // the typed shorthand all produce an identical, correctly-positioned
  // result regardless of which one you use.
  const insertDivider=()=>{
    const q=quillRefs.current[activeTab];
    if(!q)return;
    insertHorizontalLine(q);
  };

  const applyMerge=(text,r)=>interpolateMergeTags(text,r||{},settings||{});

  const [sendResults,setSendResults]=useState(null); // {sent, failed, errors:[{email,error}]}
  const sendMerge=async()=>{
    if(!cur.subject){alert("Enter a subject line.");return;}
    if(!recipients.length){alert("No recipients loaded.");return;}
    setSending(true);setSendResults(null);
    let sent=0;const errors=[];
    const token=localStorage.getItem("sdr_token");
    const h={"Content-Type":"application/json","Authorization":"Bearer "+(token||"")};
    for(const r of recipients){
      try{
        const res=await fetch("/api/email/send",{method:"POST",headers:h,body:JSON.stringify({from:fromEmail||settings.senderEmail||"",senderEmail:fromEmail||settings.senderEmail||"",to:r.email,subject:applyMerge(cur.subject,r),body:applyMerge(cur.body,r),fromName:settings.sdrName||""})});
        if(res.ok)sent++;
        else{const d=await res.json().catch(()=>({}));errors.push({email:r.email,error:d.error||("HTTP "+res.status)});}
      }catch(e){errors.push({email:r.email,error:e.message});}
      await new Promise(res=>setTimeout(res,400)); // rate-limit guard, matches the pace used elsewhere for bulk sends
    }
    setSending(false);
    setSendResults({sent,failed:errors.length,errors});
  };
  const FIELDS=mergeTags.length?mergeTags:["{{FirstName}}","{{LastName}}","{{Title}}","{{Company}}","{{Email}}","{{SenderName}}","{{SenderCompany}}"];
  const C2={bg:"#F3F2F1",white:"#fff",border:"#D1CEC9",accent:"#0078D4",accentL:"#DEECF9",text:"#201F1E",textSub:"#605E5C",textMuted:"#A19F9D",green:"#107C10",red:"#A4262C"};

  return(
    <div style={{position:"fixed",inset:0,zIndex:9999,display:"flex",flexDirection:"column",background:C2.bg,fontFamily:"'Segoe UI',Calibri,sans-serif",animation:closing?"mmSlideOut .18s ease forwards":"mmSlideIn .22s cubic-bezier(.16,1,.3,1)"}}>
      {/* Title bar */}
      <div style={{background:"linear-gradient(135deg,#0078D4,#106EBE)",display:"flex",alignItems:"center",minHeight:36,flexShrink:0,userSelect:"none",boxShadow:"0 2px 8px rgba(0,120,212,.3)"}}>




        <div style={{flex:1}}/>
        <button onClick={()=>setPreview(!preview)} style={{background:preview?"rgba(255,255,255,.3)":"rgba(255,255,255,.1)",border:"none",color:"#fff",borderRadius:3,padding:"3px 10px",fontSize:11,cursor:"pointer",fontWeight:preview?600:400,margin:"0 4px"}}>{preview?"Edit Mode":"Preview"}</button>
        <button onClick={sendMerge} disabled={sending} style={{background:"#107C10",color:"#fff",border:"none",borderRadius:3,padding:"3px 10px",fontSize:11,cursor:sending?"not-allowed":"pointer",fontWeight:600,margin:"0 4px",opacity:sending?.6:1}}>{sending?"Sending...":"Finish & Merge"}</button>



      </div>

      {/* Document tabs */}
      <div style={{background:"#E4E4E4",borderBottom:"1px solid "+C2.border,display:"flex",alignItems:"flex-end",padding:"4px 8px 0",gap:2,flexShrink:0,overflowX:"auto"}}>
        {tabs.map(tab=>(
          <div key={tab.id} onClick={()=>setActiveTab(tab.id)}
            style={{background:activeTab===tab.id?"#fff":C2.bg,border:"1px solid "+(activeTab===tab.id?C2.border:"transparent"),borderBottom:activeTab===tab.id?"1px solid #fff":"none",borderRadius:"4px 4px 0 0",padding:"5px 12px 6px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:12,color:activeTab===tab.id?C2.accent:C2.textSub,fontWeight:activeTab===tab.id?600:400,minWidth:140,position:"relative",top:1,flexShrink:0}}>
            <span style={{fontSize:11}}></span>
            <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tab.subject||tab.title}</span>
            <button onClick={e=>closeTab(tab.id,e)} style={{background:"none",border:"none",cursor:"pointer",color:C2.textMuted,fontSize:14,padding:"0 2px",lineHeight:1}} onMouseEnter={e=>e.currentTarget.style.color=C2.red} onMouseLeave={e=>e.currentTarget.style.color=C2.textMuted}></button>
          </div>
        ))}
        <button onClick={addTab} style={{background:"transparent",border:"none",cursor:"pointer",color:C2.textSub,fontSize:20,padding:"0 8px",borderRadius:"4px 4px 0 0",lineHeight:1}} title="New tab">+</button>
      </div>

      {/* Editor area */}
      <div style={{flex:1,display:"flex",justifyContent:"center",background:"#fff",overflow:"auto",padding:"0"}}>
        <div style={{width:"100%",maxWidth:"100%",display:"flex",flexDirection:"column",gap:0}}>
          <div style={{background:"#fff",border:"1px solid "+C2.border,borderBottom:"none",borderRadius:"4px 4px 0 0"}}>
            {/* Outlook-style layout */}
            <div style={{display:"flex",flexDirection:isMobile?"column":"row",alignItems:isMobile?"stretch":"stretch"}}>
              {/* Send button - left side like Outlook (top, on mobile) */}
              <div style={{display:"flex",flexDirection:isMobile?"row":"column",alignItems:"center",justifyContent:isMobile?"flex-start":"center",padding:"12px 16px",borderRight:isMobile?"none":"1px solid #e0e0e0",borderBottom:isMobile?"1px solid #e0e0e0":"none",flexShrink:0,minWidth:isMobile?"auto":80}}>
                <button onClick={sendMerge} disabled={sending} style={{display:"flex",flexDirection:isMobile?"row":"column",alignItems:"center",gap:isMobile?8:4,background:"none",border:"1px solid #c0c0c0",borderRadius:6,cursor:sending?"not-allowed":"pointer",padding:"8px 14px",minWidth:60,transition:"all 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.background="#EBF3FB";e.currentTarget.style.borderColor="#0078D4";}} onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.borderColor="#c0c0c0";}}>
                  <div style={{fontSize:28,color:"#0078D4"}}>&#9658;</div>
                  <span style={{fontSize:12,color:"#0078D4",fontWeight:600,fontFamily:"Segoe UI,sans-serif"}}>{sending?"...":"Send"}</span>
                </button>
              </div>
              {/* Fields - right side */}
              <div style={{flex:1,display:"flex",flexDirection:"column"}}>
                <div style={{display:"flex",alignItems:"center",borderBottom:"1px solid #e8e8e8",minHeight:32}}>
                  <button style={{minWidth:80,padding:"5px 10px",fontSize:12,fontWeight:400,color:"#333",border:"1px solid #c0c0c0",borderRadius:3,flexShrink:0,background:"#fff",cursor:"pointer",textAlign:"center",fontFamily:"Segoe UI,sans-serif",outline:"none",margin:"3px 8px"}}>From</button>
                  <select value={fromEmail} onChange={e=>setFromEmail(e.target.value)} style={{flex:1,border:"none",outline:"none",padding:"6px 12px",fontSize:13,color:C2.text,background:"transparent",fontFamily:"Segoe UI,Calibri,sans-serif"}}>
                    {senders.map(s=><option key={s.id} value={s.email}>{s.name} ({s.email})</option>)}
                    {!senders.length&&<option value={settings.senderEmail||""}>{settings.sdrName} ({settings.senderEmail})</option>}
                  </select>
                </div>
                <div style={{display:"flex",alignItems:"center",borderBottom:"1px solid #e8e8e8",minHeight:32}}>
                  <button style={{minWidth:80,padding:"5px 10px",fontSize:12,fontWeight:400,color:"#333",border:"1px solid #c0c0c0",borderRadius:3,flexShrink:0,background:"#fff",cursor:"pointer",textAlign:"center",fontFamily:"Segoe UI,sans-serif",outline:"none",margin:"3px 8px"}}>To</button>
                  <input placeholder="" style={{flex:1,border:"none",outline:"none",padding:"6px 12px",fontSize:13,color:C2.text,background:"transparent",fontFamily:"Segoe UI,Calibri,sans-serif"}}/>
                  <select value={selectedFile} onChange={e=>loadFile(e.target.value)} style={{border:"none",outline:"none",padding:"6px 8px",fontSize:12,color:C2.textSub,background:"transparent",maxWidth:160}}>
                    <option value="">Select contact file...</option>
                    {contactFiles.map(cf=><option key={cf.id} value={cf.name}>{cf.name} ({cf.count})</option>)}
                  </select>
                  <span style={{padding:"0 8px",fontSize:12,color:C2.textMuted,flexShrink:0}}>{recipients.length} recipients</span>
                  <button onClick={importCSV} style={{background:"#f5f5f5",border:"1px solid #e0e0e0",borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer",marginRight:4,flexShrink:0}}>Import CSV</button>
                  <button onClick={()=>setShowManualEntry(!showManualEntry)} style={{background:showManualEntry?"#DEECF9":"#f5f5f5",border:"1px solid "+(showManualEntry?"#0078D4":"#e0e0e0"),borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer",color:showManualEntry?"#0078D4":"#444",marginRight:8,flexShrink:0}}>Manual</button>
                </div>
                <div style={{display:"flex",alignItems:"center",borderBottom:"1px solid #e8e8e8",minHeight:32}}>
                  <button style={{minWidth:80,padding:"5px 10px",fontSize:12,fontWeight:400,color:"#333",border:"1px solid #c0c0c0",borderRadius:3,flexShrink:0,background:"#fff",cursor:"pointer",textAlign:"center",fontFamily:"Segoe UI,sans-serif",outline:"none",margin:"3px 8px"}}>Cc</button>
                  <input placeholder="" style={{flex:1,border:"none",outline:"none",padding:"6px 12px",fontSize:13,color:C2.text,background:"transparent",fontFamily:"Segoe UI,Calibri,sans-serif"}}/>
                </div>
                <div style={{display:"flex",alignItems:"center",minHeight:32}}>
                  <button style={{minWidth:80,padding:"5px 10px",fontSize:12,fontWeight:400,color:"#333",border:"1px solid #c0c0c0",borderRadius:3,flexShrink:0,background:"#fff",cursor:"pointer",textAlign:"center",fontFamily:"Segoe UI,sans-serif",outline:"none",margin:"3px 8px"}}>Bcc</button>
                  <input placeholder="" style={{flex:1,border:"none",outline:"none",padding:"6px 12px",fontSize:13,color:C2.text,background:"transparent",fontFamily:"Segoe UI,Calibri,sans-serif"}}/>
                </div>
              </div>
            </div>
            {showManualEntry&&(
              <div style={{display:"flex",alignItems:"flex-start",borderBottom:"1px solid #d0d0d0",background:"#fff"}}>
                <span style={{minWidth:80,padding:"8px 12px",fontSize:12,fontWeight:400,color:"#333",borderRight:"1px solid #d0d0d0",flexShrink:0,fontFamily:"Segoe UI,sans-serif"}}>Emails</span>
                <div style={{flex:1,padding:"4px 8px"}}><textarea value={manualEmails} onChange={e=>setManualEmails(e.target.value)} placeholder="Paste or type emails separated by commas" rows={2} style={{width:"100%",border:"none",outline:"none",fontSize:12,color:C2.text,resize:"vertical",fontFamily:"Segoe UI,sans-serif",background:"transparent",boxSizing:"border-box"}}/></div>
              </div>
            )}
            <div style={{display:"flex",alignItems:"center",borderTop:"1px solid #d0d0d0",minHeight:32}}>
              <span style={{minWidth:80,padding:"6px 12px",fontSize:12,fontWeight:400,color:"#333",borderRight:"1px solid #d0d0d0",flexShrink:0,fontFamily:"Segoe UI,sans-serif"}}>Subject</span>
              <input ref={el=>{subjectRefs.current[activeTab]=el;}}
                value={preview&&previewContact?applyMerge(cur.subject,previewContact):cur.subject}
                onFocus={()=>{lastFocRef.current="subject";}}
                onChange={e=>!preview&&upd({subject:e.target.value,title:e.target.value||cur.title})}
                readOnly={!!preview}
                placeholder=""
                style={{border:"none",outline:"none",padding:"6px 12px",flex:1,fontSize:14,fontWeight:500,color:C2.text,fontFamily:"'Segoe UI',Calibri,sans-serif",background:"transparent"}}/>
            </div>
            {(cur.attachments||[]).length>0&&<div style={{display:"flex",alignItems:"center",borderTop:"1px solid #d0d0d0",minHeight:32,padding:"4px 8px",gap:8,flexWrap:"wrap"}}>
              <span style={{minWidth:80,padding:"4px",fontSize:11,color:"#333",flexShrink:0}}> Attachments:</span>
              {(cur.attachments||[]).map((f,i)=>(<span key={i} style={{display:"flex",alignItems:"center",gap:4,background:"#EBF3FB",border:"1px solid #C7DFF7",borderRadius:4,padding:"2px 8px",fontSize:11,color:"#0078D4"}}> {f.name}<button onClick={()=>upd({attachments:(cur.attachments||[]).filter((_,j)=>j!==i)})} style={{background:"none",border:"none",cursor:"pointer",color:"#a00",fontSize:12,padding:"0 2px"}}></button></span>))}
            </div>}
          </div>
          {/* Field bar - collapsible */}
          <div style={{background:"#EBF3FB",border:"1px solid #C7DFF7",borderBottom:"none",padding:"4px 12px",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,fontWeight:700,color:C2.textSub,textTransform:"uppercase",letterSpacing:".4px",flexShrink:0}}>Quick Insert:</span>
            <select onChange={e=>{if(e.target.value){insertField(e.target.value);e.target.value="";}}} style={{border:"1px solid #C7DFF7",borderRadius:3,padding:"2px 8px",fontSize:11,color:C2.accent,background:C2.accentL,cursor:"pointer",outline:"none"}}>
              <option value="">Select field...</option>
              {FIELDS.map(f=>(<option key={f} value={f}>{f}</option>))}
            </select>
            <select onChange={e=>{if(e.target.value==="divider"){insertDivider();}else if(e.target.value==="greeting"){lastFocRef.current="body";insertField("Hi {{FirstName}},");}else if(e.target.value==="address"){lastFocRef.current="body";insertField("{{FirstName}} {{LastName}}\n{{Title}}, {{Company}}");}else if(e.target.value==="signature"){const q=quillRefs.current[activeTab];if(q){const idx=q.getLength();q.insertText(idx,"\n\nBest regards,\n"+(settings.sdrName||"Your Name")+"\n"+(settings.company||""));}}e.target.value="";}} style={{border:"1px solid #C7DFF7",borderRadius:3,padding:"2px 8px",fontSize:11,color:C2.accent,background:C2.accentL,cursor:"pointer",outline:"none"}}>
              <option value="">Insert block...</option>
              <option value="divider">Divider</option>
              <option value="greeting">Greeting</option>
              <option value="address">Address Block</option>
              <option value="signature">Signature</option>
            </select>
            <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",background:"#fff",border:"1px solid #C7DFF7",borderRadius:3,padding:"2px 8px",fontSize:11,color:"#0078D4",flexShrink:0,marginLeft:"auto"}}> Attach<input type="file" multiple style={{display:"none"}} onChange={e=>{const files=Array.from(e.target.files);upd({attachments:[...(cur.attachments||[]),...files]});e.target.value="";}}/>  </label>
          </div>
          <div style={{background:"#fff",border:"1px solid "+C2.border,boxShadow:"0 4px 20px rgba(0,0,0,.12)",minHeight:500,display:"flex",flexDirection:"column",borderRadius:"0 0 4px 4px"}}>
            {preview&&previewContact?(
              <div style={{flex:1,padding:"48px 72px",fontSize:14,lineHeight:1.8,color:C2.text,fontFamily:"Calibri,sans-serif"}}
                dangerouslySetInnerHTML={{__html:applyMerge(cur.body,previewContact)||"<p style='color:#a19f9d'>No content yet.</p>"}}/>
            ):(
              <div className="mm-quill" style={{flex:1,display:"flex",flexDirection:"column"}}>
                <div style={{background:"#F8F7F6",borderBottom:"1px solid "+C2.border,padding:"2px 4px",overflowX:"auto",flexShrink:0}}>
                  <WordRibbon getQuill={getQuill} showStyles formatMarksOn={formatMarksOn} onToggleFormatMarks={()=>setFormatMarksOn(v=>!v)} addToast={addToast} docFilename={"mail-merge-"+activeTab} onFindReplace={()=>setShowFindReplace(true)}/>
                </div>
                <RichTextEditor key={activeTab} ref={r=>{if(r){const ed=r.getEditor?.();quillRefs.current[activeTab]=ed;registerCleanPaste(ed);if(ed){const root=ed.root;root.addEventListener("keydown",e=>{if((e.ctrlKey||e.metaKey)&&e.key==="a"){e.stopPropagation();}});}}} }
                  value={cur.body||"<p><br></p>"}
                  onChange={val=>upd({body:val})}
                  onFocus={()=>{lastFocRef.current="body";}}
                  formatMarksOn={formatMarksOn}
                  placeholder=""
                  style={{flex:1}}/>
              </div>
            )}
          </div>
          {/* Status */}
          <div style={{background:C2.accent,padding:"3px 12px",display:"flex",alignItems:"center",gap:12,borderRadius:"0 0 4px 4px",marginTop:0}}>
            <span style={{fontSize:10,color:"rgba(255,255,255,.8)"}}> Mail Merge Email</span>
            <span style={{fontSize:10,color:"rgba(255,255,255,.8)"}}>Recipients: <strong style={{color:"#fff"}}>{recipients.length}</strong></span>
            {preview&&previewContact&&(
              <MergePreviewBar recipient={previewContact} index={previewIdx} total={recipients.length} navigable={recipients.length>1} compact
                theme={{text:"#fff",textSub:"#fff",textMuted:"rgba(255,255,255,.7)",border:"rgba(255,255,255,.4)",accent:"rgba(255,255,255,.95)",accentL:"rgba(255,255,255,.15)"}}
                onFirst={()=>setPreviewIdx(0)} onPrev={()=>setPreviewIdx(p=>Math.max(0,p-1))}
                onNext={()=>setPreviewIdx(p=>Math.min(recipients.length-1,p+1))} onLast={()=>setPreviewIdx(recipients.length-1)}/>
            )}
            <span style={{flex:1}}/>
            <span style={{fontSize:10,color:"rgba(255,255,255,.7)"}}>SDR ReachOut</span>
          </div>
        </div>
      </div>
      {sendResults&&(
        <Modal open={!!sendResults} onClose={()=>setSendResults(null)} width={460} ariaLabel="Mail Merge Results">
          <div style={{padding:24,maxHeight:"70vh",overflowY:"auto",fontFamily:"'Segoe UI',sans-serif"}}>
            <h3 style={{margin:"0 0 12px",fontSize:16}}>Mail Merge Results</h3>
            <p style={{fontSize:13,margin:"0 0 12px"}}>
              <span style={{color:C2.green,fontWeight:600}}>{sendResults.sent} sent successfully</span>
              {sendResults.failed>0&&<span style={{color:C2.red,fontWeight:600}}> · {sendResults.failed} failed</span>}
            </p>
            {sendResults.failed>0&&(
              <div style={{background:"#FDF3F4",border:"1px solid "+C2.red+"33",borderRadius:4,padding:10,marginBottom:12}}>
                <p style={{fontSize:12,fontWeight:600,margin:"0 0 6px",color:C2.red}}>Failed recipients:</p>
                {sendResults.errors.map((e,i)=>(
                  <div key={i} style={{fontSize:12,padding:"3px 0",borderBottom:i<sendResults.errors.length-1?"1px solid #00000011":"none"}}>
                    <b>{e.email}</b> — {e.error}
                  </div>
                ))}
              </div>
            )}
            <button onClick={()=>setSendResults(null)} style={{background:C2.accent,color:"#fff",border:"none",borderRadius:4,padding:"8px 18px",fontSize:13,cursor:"pointer"}}>Close</button>
          </div>
        </Modal>
      )}
      <FindReplaceDialog open={showFindReplace} onClose={()=>setShowFindReplace(false)} getQuill={getQuill} addToast={addToast} navTheme={C2.nav}/>
    </div>
  );
}

export default function MailMerge({settings={},addToast=()=>{}}){
  const [showMailMergeEditor,setShowMailMergeEditor]=useState(false);
  const [mailMergeEditorClosing,setMailMergeEditorClosing]=useState(false);
  const closeMailMergeEditor=()=>{
    setMailMergeEditorClosing(true);
    setTimeout(()=>{setShowMailMergeEditor(false);setMailMergeEditorClosing(false);},180);
  };
  const [activeStep,setActiveStep]=useState("initial");
  const [ribbonTab,setRibbonTab]=useState("mailings");
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<=768);
  useEffect(()=>{
    const onResize=()=>setIsMobile(window.innerWidth<=768);
    window.addEventListener("resize",onResize);
    return ()=>window.removeEventListener("resize",onResize);
  },[]);
  
  const [recipients,setRecipients]=useState([]);
  const [filteredRecipients,setFilteredRecipients]=useState([]);
  const [contactFiles,setContactFiles]=useState([]);
  const [senders,setSenders]=useState([]);
  const [selectedFile,setSelectedFile]=useState("");
  const [manualEmails,setManualEmails]=useState("");
  const [showManualEntry,setShowManualEntry]=useState(false);
  // Field mapping — "Map Data" per the redesign plan. Used to be a hidden
  // step: importCSV silently ran every row through a fixed alias list
  // (normalizeRow, below) and any column it didn't recognize just vanished
  // with no way to tell it what a column meant, and "Match Fields" only
  // ever toasted the detected column names read-only. Import now pauses on
  // a real mapping grid — importRawRows/importFileName hold the just-parsed
  // file (original headers intact) until the user confirms or adjusts the
  // mapping in showMapFields below.
  const [showMapFields,setShowMapFields]=useState(false);
  const [importRawRows,setImportRawRows]=useState([]);
  const [importFileName,setImportFileName]=useState("");
  const [fieldMapping,setFieldMapping]=useState({first:"",last:"",name:"",email:"",company:"",title:""});
  const [composeWindows,setComposeWindows]=useState([]);
  const openComposeWindow=()=>{
    setComposeWindows(p=>[...p,{id:Date.now()+Math.random()}]);
  };
  const closeComposeWindow=(id)=>{
    setComposeWindows(p=>p.filter(w=>w.id!==id));
  };
  const sendComposeEmail=async(payload)=>{
    const recipients=[...payload.to,...payload.cc,...payload.bcc];
    const failures=[];
    for(const to of recipients){
      const res=await fetch("/api/email/send",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        to,from:payload.from,fromName:settings.sdrName,subject:payload.subject,body:payload.body,
      })});
      if(!res.ok){
        const d=await res.json().catch(()=>({}));
        failures.push(to+": "+(d.error||("HTTP "+res.status)));
      }
    }
    if(failures.length)throw new Error(failures.join("; "));
  };
  const [fromEmail,setFromEmail]=useState("");
  const [previewIdx,setPreviewIdx]=useState(0);
  const [previewMode,setPreviewMode]=useState(false);
  // Full-window Preview workspace (MailMergeFullPreview.js) — separate from
  // previewMode above, which still drives the compose column's own inline
  // read-only render (now only reachable via "Highlight Merge Fields", a
  // pre-existing mis-wired toggle left untouched). previewIdx/activeStep are
  // only ever *read* as the seed the full-window view opens on; it owns its
  // own recipient/step navigation from there so nothing here can affect
  // send/personalization state.
  const [showFullPreview,setShowFullPreview]=useState(false);
  const [searchQ,setSearchQ]=useState("");
  const [sending,setSending]=useState(false);
  const [scheduling,setScheduling]=useState(false);
  const [showRules,setShowRules]=useState(false);
  const [showTemplateLib,setShowTemplateLib]=useState(false);
  const [templateLibFilter,setTemplateLibFilter]=useState("all"); // "all" | "fresh" | "followup" | "general" — matches the currently-open step by default whenever the library is opened, see the button below
  const [templateCtxMenu,setTemplateCtxMenu]=useState(null); // {x,y,template}
  // Phase 4b: showTemplateLib/templateCtxMenu/showMapFields (along with
  // showRotation/showMmVersions/showRules/showWordOptions/showPasteOptions/
  // sendResults below) now use the shared <Modal>/<ContextMenu>, which each
  // own their own internal ref + useModalA11y call — the standalone
  // refs/hooks that used to live here are removed accordingly.
  const [renamingTemplateId,setRenamingTemplateId]=useState(null);
  const [renameDraft,setRenameDraft]=useState("");
  const [aiLoading,setAiLoading]=useState(false);
  const [mergeStats,setMergeStats]=useState({sent:0,delivered:0,opens:0,clicks:0,replies:0,bounces:0});
  const [rule,setRule]=useState({field:"Company",operator:"equals",value:"",ifContent:"",elseContent:""});
  // Local cache paints instantly (loadTemplates), then the real repository
  // (server.js's /api/templates — see emailTemplates.js) is fetched right
  // after and replaces it — this is now a shared, tenant-wide library, not
  // just this browser's localStorage, so it also picks up templates saved
  // from the Templates sidebar page or from Sequences/Workspace.
  const [templates,setTemplates]=useState(()=>loadTemplates(DEFAULT_TEMPLATES));
  useEffect(()=>{ fetchTemplateRepo().then(list=>{ if(list.length||templates.length===0) setTemplates(list); }); },[]); // eslint-disable-line react-hooks/exhaustive-deps
  // Persist every Save/Duplicate/Rename/Delete to the shared local cache so
  // Workspace's "Templates" view (and this window, next time it opens) sees
  // the same list instantly. Fires the browser's native `storage` event in
  // any other same-origin window that's open right now, too — the server
  // fetch above is the source of truth; this is just the fast, same-tab
  // cross-window mirror of whatever that fetch (or a save) most recently
  // resolved to.
  useEffect(()=>{ saveTemplates(templates); },[templates]);
  const [drafts,setDrafts]=useState(()=>{
    const d={};
    FOLLOW_UPS.forEach(fu=>{d[fu.id]={subject:"",body:"",delay:fu.day};});
    return d;
  });
  // A Quill body that "looks empty" is never really `""` — Quill's own
  // empty-document markup is `<p><br></p>`, which is a truthy string, so a
  // plain `!drafts.initial.body` check both under- and over-reports: it
  // never catches a body that's visually empty apart from formatting tags,
  // and (the bug actually reported) it can't tell the caller WHICH of
  // subject/body is the real problem when only one is missing — e.g. right
  // after importing a Word document into the body via the ribbon's Import
  // button, which only ever fills the body and has no way to know what
  // email subject you want, leaving Send blocked by a message ("Write the
  // initial email (subject & body) first") that reads as if the body text
  // the user can plainly see was somehow not registered at all.
  const isMailMergeBodyEmpty=(html)=>{
    if(!html)return true;
    return !html.replace(/<[^>]*>/g,"").replace(/&nbsp;/g," ").trim();
  };
  const initialEmailIssue=()=>{
    const subjectEmpty=!drafts.initial.subject||!drafts.initial.subject.trim();
    const bodyEmpty=isMailMergeBodyEmpty(drafts.initial.body);
    if(subjectEmpty&&bodyEmpty)return "Write the initial email (subject & body) first";
    if(subjectEmpty)return "The Initial Email is missing a subject line — the body content is fine, just add a subject to send";
    if(bodyEmpty)return "The Initial Email body is empty — add content before sending";
    return null;
  };
  const [subjectFocus,setSubjectFocus]=useState(false);
  const [showWordOptions,setShowWordOptions]=useState(false);
  const [showFindReplace,setShowFindReplace]=useState(false);
  const [wordCount,setWordCount]=useState({words:0,chars:0,readTime:"0 min"});
  const [wordOptions,setWordOptions]=useState({autoSave:true,spellCheck:true,grammar:true,smartSelect:true,dragDrop:true,ctrlClick:true,autoComplete:true,trackFormatting:false,showRuler:true,showFormattingMarks:false,defaultFont:"Calibri",defaultSize:"12",pasteDefault:"keepSource"});
  const [showPasteOptions,setShowPasteOptions]=useState(false);
  const [pastePos,setPastePos]=useState({x:0,y:0});
  const [openTabs,setOpenTabs]=useState(["initial"]);
  const [minimized,setMinimized]=useState({});
  const [maximized,setMaximized]=useState({});
  const [stepFlags,setStepFlags]=useState({}); // per-step {flagged,important,complete,category} — real, session-scoped compose metadata
  const [showCategoryMenu,setShowCategoryMenu]=useState(false);
  const [zoomPct,setZoomPct]=useState(100);
  const [showScheduleMenu,setShowScheduleMenu]=useState(false);
  // Window chrome mode — "full" (default): this popup opens full screen and
  // the window-chrome-style Email step tabs strip above the ribbon is
  // hidden, matching a real full-screen document view. "windowed": the step
  // tabs are visible again, entered by clicking Minimize (or by leaving
  // browser fullscreen any other way — Esc, browser chrome, etc; see the
  // fullscreenchange listener below). Deliberately NOT derived directly from
  // document.fullscreenElement, since the real Fullscreen API requires a
  // user gesture and silently fails when requested on mount — this state is
  // the source of truth for tab visibility regardless of whether the actual
  // browser fullscreen request below succeeds.
  const [chromeMode,setChromeMode]=useState("full");
  // Open state for the three ribbon dropdowns below (Insert Merge Field,
  // Schedule, Categorize) — each still needs its state declared here rather
  // than left to <RibbonDropdownButton>'s own default internal state
  // because a success handler elsewhere (scheduleMergeSend) closes Schedule
  // itself, and consistency keeps all three the same shape. The anchor refs
  // + portal/clipping-escape wiring these dropdowns used to hand-roll now
  // live inside <RibbonDropdownButton> (see RibbonPrimitives.js).
  const [showMergeFieldMenu,setShowMergeFieldMenu]=useState(false);
  const [scheduleAt,setScheduleAt]=useState("");
  const [savingDraft,setSavingDraft]=useState(false);
  const subjectRef=useRef(null);
  const fileSelectRef=useRef(null);
  // Subject Rotation Engine (Beta): an additive, opt-in sending path backed
  // by a server-side MailMergeRun. The existing "Finish & Merge" flow above
  // is untouched — this is a second, separate way to send that gains a
  // permanently-pinned per-recipient subject, pause/resume, and rotation
  // analytics by going through the new /api/mailmerge/runs engine instead.
  const [showRotation,setShowRotation]=useState(false);
  const [mmRun,setMmRun]=useState(null); // { id, status, sent, failed, totalRecipients, statusCounts }
  // Stage-rail progress signals (see MailMergeStageRail above) — each is
  // set exactly once, at the one real place that stage genuinely completes,
  // not inferred/guessed from unrelated state.
  const [aiPersonalizationUsed,setAiPersonalizationUsed]=useState(false);
  const [hasPreviewed,setHasPreviewed]=useState(false);
  const [emailsValidated,setEmailsValidated]=useState(false);
  useEffect(()=>{ if(previewMode)setHasPreviewed(true); },[previewMode]);
  // Hide/unhide for the stage rail — persisted the same way Workspace's own
  // pane layout persists (localStorage, restored on mount) so collapsing it
  // once keeps it collapsed next time this window opens.
  const [railCollapsed,setRailCollapsed]=useState(()=>{
    try{ return localStorage.getItem("sdr_mm_rail_collapsed")==="1"; }catch(e){ return false; }
  });
  useEffect(()=>{
    try{ localStorage.setItem("sdr_mm_rail_collapsed", railCollapsed?"1":"0"); }catch(e){/* ignore */}
  },[railCollapsed]);
  const [creatingRun,setCreatingRun]=useState(false);
  const [runActionLoading,setRunActionLoading]=useState(false);
  // Word-style version history for the rotation-enabled run — only
  // meaningful once a run exists (mmRun.id) and only editable while it's
  // still a Draft (see server.js's PUT /mailmerge/runs/:id), matching the
  // same "don't change what's already mid-send" rule Start/Pause/Cancel above follow.
  const [showMmVersions,setShowMmVersions]=useState(false);
  const [mmVersionsList,setMmVersionsList]=useState([]);
  const [mmVersionsLoading,setMmVersionsLoading]=useState(false);
  const [mmRevertingId,setMmRevertingId]=useState(null);
  const [savingRunEdits,setSavingRunEdits]=useState(false);
  const mmAuthHeaders=()=>({"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")});
  const refreshMmRun=async(id)=>{
    try{
      const r=await fetch(`/api/mailmerge/runs/${id}`,{headers:mmAuthHeaders()});
      const d=await r.json();
      if(r.ok)setMmRun(d);
    }catch(e){/* silent — panel keeps last known state */}
  };
  const openRotationPanel=async()=>{
    setShowRotation(true);
    if(mmRun)return;
    if(!recipients.length){addToast("Load a recipient list first","error");return;}
    {const issue=initialEmailIssue();if(issue){addToast(issue,"error");return;}}
    setCreatingRun(true);
    try{
      const r=await fetch("/api/mailmerge/runs",{method:"POST",headers:mmAuthHeaders(),body:JSON.stringify({
        name:`Mail Merge — ${selectedFile||"Campaign"} — ${new Date().toLocaleDateString()}`,
        senders:(senders.length?senders.map(s=>s.email):[fromEmail]).filter(Boolean),
        subject:drafts.initial.subject,
        bodyTemplate:drafts.initial.body,
        recipients,
      })});
      const d=await r.json();
      if(!r.ok)throw new Error(d?.error||"Could not create the rotation-enabled run.");
      setMmRun(d);
    }catch(e){addToast(e.message||"Could not create the rotation-enabled run.","error");}
    setCreatingRun(false);
  };
  const runAction=async(action)=>{
    if(!mmRun)return;
    setRunActionLoading(true);
    try{
      const r=await fetch(`/api/mailmerge/runs/${mmRun.id}/${action}`,{method:"POST",headers:mmAuthHeaders()});
      const d=await r.json();
      if(!r.ok)throw new Error(d?.error||`Could not ${action} the run.`);
      setMmRun(m=>({...m,...d}));
      addToast(action==="start"?"Sending started":action==="pause"?"Sending paused":"Run canceled","success");
    }catch(e){addToast(e.message||`Could not ${action} the run.`,"error");}
    setRunActionLoading(false);
  };
  // Pushes the current Initial Email tab's subject/body up to the run so
  // the run's content — and therefore its version history — actually
  // reflects ongoing edits, not just what existed at creation time.
  const saveRunEdits=async()=>{
    if(!mmRun?.id)return;
    setSavingRunEdits(true);
    try{
      const r=await fetch(`/api/mailmerge/runs/${mmRun.id}`,{method:"PUT",headers:mmAuthHeaders(),body:JSON.stringify({
        subject:drafts.initial.subject,bodyTemplate:drafts.initial.body,
        senders:(senders.length?senders.map(s=>s.email):[fromEmail]).filter(Boolean),
      })});
      const d=await r.json();
      if(!r.ok)throw new Error(d?.error||"Could not save changes to the run.");
      setMmRun(m=>({...m,...d}));
      addToast("Run updated — a version of what it looked like before this save was kept","success");
    }catch(e){addToast(e.message||"Could not save changes to the run.","error");}
    setSavingRunEdits(false);
  };
  const loadMmVersions=async()=>{
    if(!mmRun?.id)return;
    setMmVersionsLoading(true);
    try{
      const r=await fetch(`/api/mailmerge/runs/${mmRun.id}/versions`,{headers:mmAuthHeaders()});
      const d=await r.json();
      setMmVersionsList(Array.isArray(d)?d:[]);
    }catch(e){addToast("Couldn't load version history: "+e.message,"error");}
    setMmVersionsLoading(false);
  };
  const openMmVersionHistory=()=>{setShowMmVersions(true);loadMmVersions();};
  const revertMmVersion=async(version)=>{
    if(!mmRun?.id)return;
    if(!window.confirm(`Revert to the version saved ${new Date(version.createdAt).toLocaleString()}? The run's current content will be saved as a new version first.`))return;
    setMmRevertingId(version.id);
    try{
      const r=await fetch(`/api/mailmerge/runs/${mmRun.id}/versions/${version.id}/revert`,{method:"POST",headers:mmAuthHeaders()});
      const d=await r.json();
      if(!r.ok)throw new Error(d?.error||"Revert failed.");
      setMmRun(m=>({...m,...d}));
      setDrafts(p=>({...p,initial:{...p.initial,subject:d.subject||"",body:d.bodyTemplate||""}}));
      addToast("Reverted to the "+new Date(version.createdAt).toLocaleString()+" version","success");
      setShowMmVersions(false);
    }catch(e){addToast(e.message||"Revert failed.","error");}
    setMmRevertingId(null);
  };
  useEffect(()=>{
    if(!showRotation||!mmRun?.id)return;
    if(mmRun.status!=="Sending")return;
    const iv=setInterval(()=>refreshMmRun(mmRun.id),4000);
    return ()=>clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[showRotation,mmRun?.id,mmRun?.status]);
  const recipientSearchRef=useRef(null);
  const quillRef=useRef(null);
  const lastFocRef=useRef("body");
  const [formatMarksOn,setFormatMarksOn]=useState(false);
  const getQuill=()=>quillRef.current?.getEditor?.();
  // Voice dictation (live continuous refinement) for whichever step tab is
  // active. Reads/writes drafts[activeStep] directly — sendChunk always
  // sends the *current* subject/body for that step and the returned
  // subject+body get written back into the same step, so switching tabs
  // mid-dictation naturally keeps voice input scoped to whatever tab is
  // active when a chunk finishes (finalizes async, a beat after speaking).
  const voice=useVoiceDraft({
    getQuill,
    subject:drafts[activeStep]?.subject||"",
    setSubject:(v)=>setDrafts(p=>({...p,[activeStep]:{...p[activeStep],subject:v}})),
    setBodyHtml:(html)=>setDrafts(p=>({...p,[activeStep]:{...p[activeStep],body:html}})),
    sdrSettings:settings,
    addToast,
  });
  // Persist drafts to localStorage so text survives page refreshes and
  // dev-server restarts. (Will move to backend storage at deploy time.)
  useEffect(()=>{
    try{
      localStorage.setItem("mailmerge_drafts", JSON.stringify(drafts));
    }catch(e){}
  },[drafts]);


  

  useEffect(()=>{
    const handler=(e)=>{
      if(!(e.ctrlKey||e.metaKey))return;
      const q=quillRef.current?.getEditor?.();
      if(e.key==="f"){e.preventDefault();setShowFindReplace(true);}
      else if(e.key==="h"){e.preventDefault();setShowFindReplace(true);}
      else if(e.key==="s"){e.preventDefault();addToast("Draft auto-saved","success");}
      else if(e.key==="z"&&!e.shiftKey){e.preventDefault();q?.history.undo();}
      else if(e.key==="y"||e.key==="Z"){e.preventDefault();q?.history.redo();}
      else if(e.key==="a"){e.preventDefault();q?.setSelection(0,q.getLength());}
      // Ctrl/Cmd+Enter — Send, matching Workspace's own shortcut (see
      // keyboardShortcuts.js's new scope:"mailmerge" entries). Goes through
      // finishSendRef rather than calling finishSend directly: this effect
      // has an empty dep array (registers the listener once), so a direct
      // reference would close over the mount-time finishSend and read
      // stale recipients/drafts state on every later keypress. The ref is
      // kept current by the effect right after finishSend's own definition.
      else if(e.key==="Enter"){e.preventDefault();finishSendRef.current?.();}
    };
    document.addEventListener("keydown",handler);
    return()=>document.removeEventListener("keydown",handler);
  },[]);

  // Open full screen by default. Best-effort only — the Fullscreen API
  // requires transient user activation and commonly rejects when called on
  // mount (the opener's click doesn't reliably carry over), so this is
  // wrapped in a no-op catch. chromeMode above (defaulting to "full") is the
  // real source of truth for hiding the Email step tabs, independent of
  // whether this actually succeeds.
  useEffect(()=>{
    document.documentElement.requestFullscreen?.().catch(()=>{});
  },[]);

  // Keep chromeMode in sync when fullscreen is exited by means other than
  // the Minimize button — Esc key, the browser's own "exit fullscreen"
  // control, etc. — so the step tabs reliably reappear whenever the window
  // is genuinely no longer full screen.
  useEffect(()=>{
    const onFsChange=()=>{ if(!document.fullscreenElement) setChromeMode("windowed"); };
    document.addEventListener("fullscreenchange",onFsChange);
    return()=>document.removeEventListener("fullscreenchange",onFsChange);
  },[]);

  useEffect(()=>{
    fetch("/api/contact-files",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}).then(r=>r.json()).then(d=>{if(Array.isArray(d))setContactFiles(d);}).catch(()=>{});
    fetch("/api/team/senders",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}).then(r=>r.json()).then(d=>{if(Array.isArray(d)){setSenders(d);if(d[0])setFromEmail(d[0].email);}}).catch(()=>{});
  },[]);

  // Phase 5 cross-window navigation — Mail Merge's half of the
  // ?focusContact= handling OutlookWorkspace.js already had from Phase 4.
  // Mail Merge has no per-contact "conversation" to jump into the way
  // Workspace does, so the closest equivalent is: find which contact file
  // this email lives in, load it (same as picking it from the Recipients
  // dropdown), then jump the Live Preview navigator straight to that
  // recipient. `pendingFocusEmail` bridges loadFile()'s async setRecipients
  // — the second effect below watches `recipients` and only resolves the
  // jump once the newly-loaded list actually contains this email.
  const [pendingFocusEmail,setPendingFocusEmail]=useState(null);
  const focusContactAppliedRef=useRef(false);
  useEffect(()=>{
    if(focusContactAppliedRef.current)return;
    const email=new URLSearchParams(window.location.search).get("focusContact");
    if(!email)return;
    focusContactAppliedRef.current=true;
    window.history.replaceState({},"",window.location.pathname);
    (async()=>{
      try{
        const r=await fetch("/api/leads?limit=10000",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
        const all=await r.json();
        const lead=Array.isArray(all)?all.find(l=>(l.email||"").toLowerCase()===email.toLowerCase()):null;
        if(!lead||!lead.contactFile){
          addToast("Couldn't find "+email+" in any contact file to preview here.","info");
          return;
        }
        setPendingFocusEmail(email);
        await loadFile(lead.contactFile);
      }catch(e){addToast("Couldn't load that contact's mail merge preview.","error");}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  useEffect(()=>{
    if(!pendingFocusEmail||!recipients.length)return;
    const idx=recipients.findIndex(r=>(r.email||"").toLowerCase()===pendingFocusEmail.toLowerCase());
    if(idx>=0){setPreviewIdx(idx);setPreviewMode(true);}
    setPendingFocusEmail(null);
  },[pendingFocusEmail,recipients]);

  useEffect(()=>{
    if(!searchQ){setFilteredRecipients(recipients);return;}
    const q=searchQ.toLowerCase();
    setFilteredRecipients(recipients.filter(r=>(r.name||"").toLowerCase().includes(q)||(r.email||"").toLowerCase().includes(q)||(r.company||"").toLowerCase().includes(q)));
  },[searchQ,recipients]);

  const loadFile=async(fileName)=>{
    if(!fileName)return;
    setSelectedFile(fileName);
    try{
      const r=await fetch("/api/leads?limit=10000",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const all=await r.json();
      const suppressedCount=Array.isArray(all)?all.filter(l=>l.contactFile===fileName&&l.suppressed).length:0;
      const filtered=Array.isArray(all)?all.filter(l=>l.contactFile===fileName&&!l.suppressed).slice(0,500):[];
      setRecipients(filtered);
      setFilteredRecipients(filtered);
      addToast(`Loaded ${filtered.length} recipients from ${fileName}`+(suppressedCount?` (${suppressedCount} excluded — already replied, bounced, or complained)`:""),"success");
    }catch(e){addToast("Failed to load recipients","error");}
  };

  // Alias groups used both by the auto-detector below (best-guess mapping
  // shown pre-filled in the grid) and by normalizeRow (the fallback used
  // wherever a raw row is normalized without going through the mapping UI —
  // e.g. nowhere currently, but kept so the two stay in sync if a future
  // caller needs a no-UI normalize path again).
  const FIELD_ALIASES={
    email:["email","email address","work email","e-mail"],
    first:["first","firstname","first name"],
    last:["last","lastname","last name"],
    name:["name","full name","contact name"],
    company:["company","company name","organization","account"],
    title:["title","job title","position","role"],
  };
  // Best-guess column mapping for the grid — same alias groups as before,
  // just returning which literal column header matched (if any) instead of
  // silently reading the value. Order-preserves whatever headers the file
  // actually had, so an unrecognized column (e.g. "Business Email", "Org")
  // shows up as an option the user can map by hand instead of vanishing.
  const detectFieldMapping=(headers)=>{
    const lowerToOriginal={};
    headers.forEach(h=>{ const k=(h||"").trim().toLowerCase(); if(!(k in lowerToOriginal))lowerToOriginal[k]=h; });
    const pick=(field)=>{ for(const alias of FIELD_ALIASES[field]){ if(lowerToOriginal[alias]!==undefined)return lowerToOriginal[alias]; } return ""; };
    return {first:pick("first"),last:pick("last"),name:pick("name"),email:pick("email"),company:pick("company"),title:pick("title")};
  };

  // Maps a raw parsed row (arbitrary column names) into our recipient shape,
  // given an explicit column mapping (target field → source header, or ""
  // for unmapped) rather than re-guessing aliases — the mapping grid is the
  // only caller now (see applyFieldMapping below), but this stays a plain
  // function of (row, mapping) so it's easy to unit-test independent of the
  // modal's state.
  const normalizeRow=(row,idx,mapping)=>{
    const get=(col)=>{ if(!col)return ""; const v=row[col]; return v===undefined||v===null?"":String(v).trim(); };
    const email=get(mapping.email);
    const first=get(mapping.first);
    const last=get(mapping.last);
    const fullName=get(mapping.name)||[first,last].filter(Boolean).join(" ");
    // Any imported column not claimed by the standard field mapping above
    // (e.g. "Industry", "Deal Size" — real columns the mapping grid left
    // unmapped because they're not one of the 6 standard fields) is kept
    // as a customField rather than discarded. interpolateMergeTags
    // (mergeTags.js) already resolves {{ColumnName}} via
    // contact.customFields — that support has existed all along, this
    // just stops normalizeRow from throwing the data away before it ever
    // reaches the merge engine. Surfaced in the Insert Merge Field
    // dropdown's "Other Fields" section below, same as Use Existing
    // List's extra Contact fields already are.
    const mappedCols=new Set(Object.values(mapping).filter(Boolean));
    const customFields={};
    Object.keys(row).forEach(col=>{
      if(mappedCols.has(col))return;
      const v=row[col];
      const s=v===undefined||v===null?"":String(v).trim();
      if(s)customFields[col]=s;
    });
    return {
      id:idx+1,
      name:fullName,
      first:first||fullName.split(" ")[0]||"",
      last:last||fullName.split(" ").slice(1).join(" ")||"",
      email,
      company:get(mapping.company),
      title:get(mapping.title),
      ...(Object.keys(customFields).length?{customFields}:{}),
    };
  };

  // Parses pasted/typed emails (comma or newline separated) into recipients.
  const addManualRecipients=()=>{
    const raw=manualEmails.split(/[\n,;]+/).map(s=>s.trim()).filter(Boolean);
    const emailRegex=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid=raw.filter(e=>emailRegex.test(e));
    const invalid=raw.filter(e=>!emailRegex.test(e));
    if(valid.length===0){addToast("No valid email addresses found","error");return;}
    const newOnes=valid.filter(e=>!recipients.some(r=>r.email.toLowerCase()===e.toLowerCase()));
    const parsed=newOnes.map((email,idx)=>({
      id:Date.now()+idx,
      name:email.split("@")[0],
      first:email.split("@")[0],
      last:"",
      email,
      company:"",
      title:"",
    }));
    const merged=[...recipients,...parsed];
    setRecipients(merged);setFilteredRecipients(merged);
    setManualEmails("");
    if(invalid.length>0){
      addToast(`Added ${parsed.length} recipients. ${invalid.length} invalid email(s) skipped.`,"info");
    }else{
      addToast(`Added ${parsed.length} recipient(s)`,"success");
    }
  };

  // Opens the field-mapping grid on a just-parsed file's raw rows (original
  // headers intact) instead of immediately normalizing — see the
  // showMapFields state comment above for why.
  const openMapFieldsFor=(rows,fileName)=>{
    if(!rows.length){addToast(fileName+" has no rows to import","error");return;}
    const headers=Object.keys(rows[0]);
    if(!headers.length){addToast(fileName+" has no columns to map","error");return;}
    setImportRawRows(rows);
    setImportFileName(fileName);
    setFieldMapping(detectFieldMapping(headers));
    setShowMapFields(true);
  };

  const importCSV=()=>{
    const inp=document.createElement("input");inp.type="file";inp.accept=".csv,.xlsx,.xls";
    inp.onchange=async(e)=>{
      const file=e.target.files[0];if(!file)return;
      addToast(`Reading ${file.name}...`,"info");
      const isExcel=/\.(xlsx|xls)$/i.test(file.name);
      try{
        if(isExcel){
          const buf=await file.arrayBuffer();
          const wb=XLSX.read(buf,{type:"array"});
          const sheet=wb.Sheets[wb.SheetNames[0]];
          const rows=XLSX.utils.sheet_to_json(sheet,{defval:""});
          openMapFieldsFor(rows,file.name);
        }else{
          Papa.parse(file,{
            header:true,
            skipEmptyLines:true,
            complete:(results)=>openMapFieldsFor(results.data||[],file.name),
            error:(err)=>{addToast("Failed to parse CSV: "+err.message,"error");},
          });
        }
      }catch(err){
        addToast("Import failed: "+err.message,"error");
      }
    };
    inp.click();
  };

  // Finalizes the mapping grid: normalizes every raw row with the confirmed
  // mapping and replaces the current recipient list, same as the old
  // immediate-import behavior did — just with a real chance to fix a
  // misdetected or unrecognized column first.
  const applyFieldMapping=()=>{
    if(!fieldMapping.email){addToast("Map an Email column before importing","error");return;}
    const parsed=importRawRows.map((row,idx)=>normalizeRow(row,idx,fieldMapping)).filter(r=>r.email);
    const skipped=importRawRows.length-parsed.length;
    setRecipients(parsed);setFilteredRecipients(parsed);
    setShowMapFields(false);
    addToast(`Imported ${parsed.length} recipients from ${importFileName}`+(skipped?` (${skipped} skipped — no email in the mapped column)`:""),parsed.length?"success":"error");
  };

  const applyMerge=(text,recipient)=>{
    if(!text||!recipient)return text||"";
    return interpolateMergeTags(text,recipient,{sdrName:settings.sdrName||"Anji Reddy",company:settings.company||"SDR ReachOut"});
  };

  const applyRule=(text,recipient)=>applyConditionalBlocks(text,recipient||{});

  const insertMergeTag=(tag)=>{
    if(lastFocRef.current==="subject"){
      const inp=subjectRef.current;
      if(inp){
        const s=inp.selectionStart||0,e=inp.selectionEnd||0;
        const cur=drafts[activeStep].subject||"";
        const newSubj=cur.slice(0,s)+tag+cur.slice(e);
        setDrafts(p=>({...p,[activeStep]:{...p[activeStep],subject:newSubj}}));
        setTimeout(()=>{inp.focus();inp.setSelectionRange(s+tag.length,s+tag.length);},10);
      }
    }else{
      const q=quillRef.current?.getEditor?.();
      if(q){const rng=q.getSelection(true);const idx=rng?rng.index:q.getLength();q.insertText(idx,tag);q.setSelection(idx+tag.length);}
    }
  };

  // Dynamic "Other Fields" for the Insert Merge Field dropdown — real
  // recipient-object keys beyond the 8 standard tags above. Populated from
  // two sources: top-level extra keys (e.g. phone/linkedin, which "Use
  // Existing List" Contact records carry directly) and customFields (the
  // extra, unmapped CSV columns normalizeRow now preserves instead of
  // discarding — see its own comment). interpolateMergeTags already
  // resolves both via its contact-property/customFields lookup fallback
  // (mergeTags.js), so surfacing them here is exposing existing behavior,
  // not adding new merge logic.
  const otherMergeFields=recipients.length
    ? Array.from(new Set([
        ...Object.keys(recipients[0]).filter(k=>!RESERVED_RECIPIENT_KEYS.has(k)&&recipients[0][k]!=null&&recipients[0][k]!==""),
        ...Object.keys(recipients[0].customFields||{}).filter(k=>recipients[0].customFields[k]!=null&&recipients[0].customFields[k]!==""),
      ]))
    : [];

  const insertRuleTemplate=()=>{
    const ruleText=`\n\n[[IF:${rule.field}=${rule.value}]]\n${rule.ifContent||"[Content for matching recipients]"}\n[[ELSE]]\n${rule.elseContent||"[Content for other recipients]"}\n[[ENDIF]]\n`;
    const q=quillRef.current?.getEditor?.();
    if(q){const idx=q.getLength();q.insertText(idx,ruleText);}
    setShowRules(false);addToast("Conditional rule inserted","success");
  };

  const aiGenerateAll=async()=>{
    if(recipients.length===0){addToast("Load recipients first","error");return;}
    setAiLoading(true);
    const sample=recipients[0];
    for(const fu of FOLLOW_UPS){
      try{
        const r=await fetch("/api/ai/sequence-step",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          lead:sample,settings,stepLabel:fu.label,stepDay:fu.day,stepDesc:"Email",
          prevEmails:FOLLOW_UPS.slice(0,FOLLOW_UPS.indexOf(fu)).map(f=>`${f.label}: ${drafts[f.id].body||""}`).join("\n\n"),
          isFollowUp:fu.id!=="initial"
        })});
        const d=await r.json();
        setDrafts(p=>({...p,[fu.id]:{...p[fu.id],subject:d.subject||"",body:d.body||""}}));
        addToast(`${fu.label} generated`,"success");
      }catch(e){}
      await new Promise(r=>setTimeout(r,400));
    }
    setAiLoading(false);addToast("All templates generated!","success");
  };

  const aiGenerateStep=async()=>{
    if(recipients.length===0){addToast("Load recipients first","error");return;}
    setAiLoading(true);
    const sample=recipients[0];
    const fuIdx=FOLLOW_UPS.findIndex(f=>f.id===activeStep);
    try{
      const r=await fetch("/api/ai/sequence-step",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        lead:sample,settings,stepLabel:FOLLOW_UPS[fuIdx].label,stepDay:FOLLOW_UPS[fuIdx].day,stepDesc:"Email",
        prevEmails:FOLLOW_UPS.slice(0,fuIdx).map(f=>`${f.label}: ${drafts[f.id].body||""}`).join("\n\n"),
        isFollowUp:fuIdx>0
      })});
      const d=await r.json();
      setDrafts(p=>({...p,[activeStep]:{...p[activeStep],subject:d.subject||"",body:d.body||""}}));
      addToast(`${FOLLOW_UPS[fuIdx].label} generated`,"success");
      setAiPersonalizationUsed(true);
    }catch(e){addToast("AI error","error");}
    setAiLoading(false);
  };

  // Used to silently only ever send to the first 5 recipients — a synchronous
  // fetch loop with a hardcoded `.slice(0,5)`, left over from before the
  // server grew a real send engine. "Rotation & Testing" (openRotationPanel
  // above) already sends through that engine (MailMergeRun +
  // MailMergeRecipient, batches of 50/tick, no cap) — this now points the
  // primary Send button at the exact same engine instead of maintaining a
  // second, capped, synchronous path. One consequence worth calling out: a
  // run this creates behaves identically to one created via "Rotation &
  // Testing", so clicking Send now also opens that same live-status panel
  // (real sent/failed counts, Pause/Cancel) instead of a fire-and-forget
  // loop — this is what "pause/resume/monitor for free" (from the redesign
  // plan) means in practice, not a separate feature.
  const finishSend=async()=>{
    if(filteredRecipients.length===0){addToast("No recipients loaded","error");return;}
    {const issue=initialEmailIssue();if(issue){addToast(issue,"error");return;}}
    if(mmRun&&mmRun.status==="Sending"){
      addToast(`Already sending — ${mmRun.sent||0} of ${mmRun.totalRecipients||0} sent so far`,"info");
      setShowRotation(true);
      return;
    }
    const senderList=(senders.length?senders.map(s=>s.email):[fromEmail]).filter(Boolean);
    if(!senderList.length){addToast("No sender mailbox available — connect one first","error");return;}
    setSending(true);
    try{
      // Reuse a not-yet-started run (e.g. one auto-created by opening
      // "Rotation & Testing" and then closing it again) instead of creating
      // a second, duplicate one for the same recipients. Anything already
      // Sending was handled by the early return above; Completed/Canceled
      // falls through to creating a fresh run below.
      let run=mmRun&&(mmRun.status==="Draft"||mmRun.status==="Paused")?mmRun:null;
      if(!run){
        const r=await fetch("/api/mailmerge/runs",{method:"POST",headers:mmAuthHeaders(),body:JSON.stringify({
          name:`Mail Merge — ${selectedFile||"Campaign"} — ${new Date().toLocaleString()}`,
          senders:senderList,
          subject:drafts.initial.subject,
          bodyTemplate:drafts.initial.body,
          recipients:filteredRecipients,
        })});
        const d=await r.json();
        if(!r.ok)throw new Error(d?.error||"Could not start the send.");
        run=d;
        setMmRun(d);
      }
      const startRes=await fetch(`/api/mailmerge/runs/${run.id}/start`,{method:"POST",headers:mmAuthHeaders()});
      const started=await startRes.json();
      if(!startRes.ok)throw new Error(started?.error||"Could not start sending.");
      setMmRun(started);
      setMergeStats(p=>({...p,sent:started.sent||0}));
      setShowRotation(true);
      addToast(`Sending started — ${started.totalRecipients||filteredRecipients.length} recipient(s) queued`,"success");
    }catch(e){addToast(e.message||"Could not start sending.","error");}
    setSending(false);
  };
  // Kept current every render so the mount-once Ctrl/Cmd+Enter listener
  // above always calls the latest finishSend (fresh recipients/drafts/mmRun
  // state), not the stale mount-time closure a direct reference would
  // capture.
  const finishSendRef=useRef(finishSend);
  useEffect(()=>{finishSendRef.current=finishSend;});

  const assignToSequence=async()=>{
    try{
      const steps=FOLLOW_UPS.map((fu,i)=>({key:`email${i+1}`,label:fu.label,day:fu.day,subject:drafts[fu.id].subject,body:drafts[fu.id].body,delay:fu.day}));
      await fetch("/api/sequences",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        name:`Mail Merge  ${selectedFile||"Campaign"}  ${new Date().toLocaleDateString()}`,
        fromEmail,steps,totalContacts:recipients.length,contactFileName:selectedFile,
      })});
      addToast("Assigned to Sequence Automation","success");
    }catch(e){addToast("Failed to create sequence","error");}
  };

  // "Create Campaign" used to be a pure no-op — onClick just toasted
  // "Campaign created from merge" without ever calling the server, so the
  // campaign it claimed to create never actually existed (didn't show up in
  // CampaignsPage.js, nothing to send/track). Wired to the real
  // POST /api/campaigns below, which requires an array of real Contact ids.
  // Recipients loaded via "Use Existing List" already carry one (loadFile
  // reads GET /api/leads, which maps each contact's Mongo _id to `.id`) —
  // those are used directly. Recipients from CSV import or manual paste only
  // have synthetic client-side ids (normalizeRow's `idx+1`, or
  // addManualRecipients' `Date.now()+idx`), so those go through the
  // existing POST /api/contacts/import first to become real Contacts,
  // exactly like a normal contact import would.
  const isRealContactId=(id)=>typeof id==="string"&&/^[0-9a-f]{24}$/i.test(id);
  const [creatingCampaign,setCreatingCampaign]=useState(false);
  const createCampaignFromMerge=async()=>{
    if(filteredRecipients.length===0){addToast("Load recipients first","error");return;}
    setCreatingCampaign(true);
    try{
      const headers={"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")};
      let contactIds=filteredRecipients.filter(r=>isRealContactId(r.id)).map(r=>r.id);
      const needsImport=filteredRecipients.filter(r=>!isRealContactId(r.id));
      if(needsImport.length){
        const importRes=await fetch("/api/contacts/import",{method:"POST",headers,body:JSON.stringify({
          contacts:needsImport,
          mapping:{firstName:"first",lastName:"last",email:"email",title:"title",company:"company"},
          fileName:selectedFile||"Mail Merge recipients",
        })});
        const importData=await importRes.json();
        if(!importRes.ok)throw new Error(importData?.error||"Could not save recipients as contacts.");
        contactIds=[...contactIds,...(importData.contactIds||[])];
        if(importData.errors>0){
          addToast(`${importData.imported} recipient(s) saved as contacts, ${importData.errors} skipped (likely already existing)`,"info");
        }
      }
      if(!contactIds.length)throw new Error("No recipients could be attached to a campaign.");
      const name=`Mail Merge — ${selectedFile||"Campaign"} — ${new Date().toLocaleString()}`;
      const campRes=await fetch("/api/campaigns",{method:"POST",headers,body:JSON.stringify({
        name,leads:contactIds,sender:fromEmail||settings.senderEmail||"",
      })});
      const campData=await campRes.json();
      if(!campRes.ok)throw new Error(campData?.error||"Could not create the campaign.");
      addToast(`Campaign "${name}" created with ${contactIds.length} contact(s)`,"success");
    }catch(e){addToast(e.message||"Could not create the campaign.","error");}
    setCreatingCampaign(false);
  };

  // Category defaults to matching whichever step is currently being saved —
  // "initial" is the fresh/first-touch email, any follow-up step is a
  // follow-up — so a template saved while editing Follow-up 2 is already
  // filed correctly for Sequences/Workspace to suggest it back at the right
  // moment, with no extra prompt in the common case. Persists to the real
  // shared repository now (server.js's /api/templates), not just this
  // browser's localStorage.
  const saveTemplate=async(categoryOverride)=>{
    const category=categoryOverride||(activeStep==="initial"?"fresh":"followup");
    const name=`Template ${templates.length+1}  ${new Date().toLocaleDateString()}`;
    try{
      const t=await createTemplateRepo({name,category,subject:drafts[activeStep].subject,body:drafts[activeStep].body});
      setTemplates(p=>[...p,t]);
      addToast("Template saved to library","success");
    }catch(e){addToast(e.message||"Could not save template","error");}
  };

  const loadTemplate=(t)=>{
    setDrafts(p=>({...p,[activeStep]:{...p[activeStep],subject:t.subject,body:t.body}}));
    setShowTemplateLib(false);addToast("Template loaded","success");
    markTemplateUsed(t.id);
  };
  // Duplicate/Rename/Delete for the template library — these were the
  // missing per-item actions flagged in the Workspace UX audit (only
  // Load/Save existed before). Now backed by the real shared repository
  // (server.js's /api/templates) instead of only this browser's
  // localStorage, so a rename/delete here is visible from Sequences,
  // Workspace, and the Templates sidebar page too.
  const duplicateTemplate=async(t)=>{
    try{
      const copy=await createTemplateRepo({name:t.name+" (Copy)",category:t.category,subject:t.subject,body:t.body});
      setTemplates(p=>[...p,copy]);
      addToast("Template duplicated","success");
    }catch(e){addToast(e.message||"Could not duplicate template","error");}
  };
  const deleteTemplate=async(id)=>{
    try{
      await deleteTemplateRepo(id);
      setTemplates(p=>p.filter(t=>t.id!==id));
      addToast("Template deleted","info");
    }catch(e){addToast(e.message||"Could not delete template","error");}
  };
  const startRenameTemplate=(t)=>{setRenamingTemplateId(t.id);setRenameDraft(t.name);};
  const commitRenameTemplate=async()=>{
    const id=renamingTemplateId;
    setRenamingTemplateId(null);
    if(!renameDraft.trim()||!id)return;
    try{
      const updated=await updateTemplateRepo(id,{name:renameDraft.trim()});
      setTemplates(p=>p.map(t=>t.id===id?updated:t));
    }catch(e){addToast(e.message||"Could not rename template","error");}
  };
  // Opens the library pre-filtered to whichever category matches the step
  // currently being edited — "based on the use choice" (fresh vs
  // follow-up), so the list starts relevant instead of one flat pile;
  // "All" is always one click away.
  //
  // BUG FIX: this used to call itself (`openTemplateLib()` from inside its
  // own body) instead of actually opening the modal — infinite recursion
  // that threw "Maximum call stack size exceeded" the instant the
  // Templates button was clicked, silently, so the library never appeared.
  // That's the exact "I can't see templates from Mail Merge" report —
  // Workspace and Sequences don't have this function at all (they open
  // their template UI a different way), which is why templates worked
  // fine there the whole time.
  const openTemplateLib=()=>{setTemplateLibFilter(activeStep==="initial"?"fresh":"followup");setShowTemplateLib(true);};

  const CATEGORY_COLORS_MM={red:"#D13438",yellow:"#FFB900",green:"#107C10",blue:"#0078D4",purple:"#8764B8"};
  const toggleStepFlag=(key)=>{
    setStepFlags(p=>({...p,[activeStep]:{...p[activeStep],[key]:!p[activeStep]?.[key]}}));
  };
  const setStepCategory=(color)=>{
    setStepFlags(p=>({...p,[activeStep]:{...p[activeStep],category:color}}));
    setShowCategoryMenu(false);
    addToast(color?"Categorized":"Category cleared","success");
  };

  // Real draft persistence for the step currently being edited — POSTs the
  // literal (unmerged) subject/body with merge tags intact to the same
  // /api/email/draft endpoint the real inbox Drafts folder reads from, so
  // it actually shows up there instead of just claiming success.
  const saveDraftReal=async()=>{
    const d=drafts[activeStep];
    if(!d.subject&&!d.body){addToast("Nothing to save for "+ (activeFU?.label||"this step"),"info");return;}
    setSavingDraft(true);
    try{
      const r=await fetch("/api/email/draft",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({senderEmail:fromEmail,subject:d.subject,body:d.body,to:""})});
      const j=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(j.error||("HTTP "+r.status));
      addToast((activeFU?.label||"Draft")+" saved to Drafts","success");
    }catch(e){addToast("Couldn't save draft: "+e.message,"error");}
    setSavingDraft(false);
  };

  // Real send-later for the whole merge batch: creates one ScheduledEmail
  // row per recipient and the server's scheduled-send poller actually sends
  // each one once its time comes. Used to silently cap at the first 5
  // recipients (a leftover copy-paste from the old finishSend's own cap,
  // fixed above) — dropped here too, since these are cheap ScheduledEmail
  // inserts, not synchronous sends, so there's no engine-migration needed
  // the way finishSend needed one.
  const scheduleMergeSend=async(when)=>{
    if(recipients.length===0){addToast("No recipients loaded","error");return;}
    {const issue=initialEmailIssue();if(issue){addToast(issue,"error");return;}}
    const sendAtDate=new Date(when);
    if(!when||isNaN(sendAtDate.getTime())||sendAtDate.getTime()<=Date.now()){addToast("Pick a time in the future","error");return;}
    setScheduling(true);
    let scheduled=0;const failed=[];
    for(const r of filteredRecipients){
      try{
        const res=await fetch("/api/email/schedule",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          to:r.email,senderEmail:fromEmail,fromName:settings.sdrName,
          subject:applyMerge(drafts.initial.subject,r),
          body:applyMerge(applyRule(drafts.initial.body,r),r),
          sendAt:sendAtDate.toISOString(),source:"mailmerge",
        })});
        if(res.ok)scheduled++;
        else{const j=await res.json().catch(()=>({}));failed.push(r.email+": "+(j.error||("HTTP "+res.status)));}
      }catch(e){failed.push(r.email+": "+e.message);}
    }
    setScheduling(false);
    if(failed.length)addToast(`Scheduled ${scheduled}, ${failed.length} failed: `+failed.join("; "),"error");
    else{addToast(`Scheduled ${scheduled} emails for ${sendAtDate.toLocaleString()}`,"success");setShowScheduleMenu(false);setScheduleAt("");}
  };

  // Real, lightweight spell/grammar check — no dictionary dependency, but
  // it actually inspects the current step's text instead of always
  // claiming success. Catches common typos, doubled words, and basic
  // spacing/capitalization slips.
  const COMMON_TYPOS={teh:"the",recieve:"receive",seperate:"separate",definately:"definitely",occured:"occurred",untill:"until",thier:"their",wich:"which",becuase:"because",alot:"a lot",adress:"address",acheive:"achieve",arguement:"argument",begining:"beginning",beleive:"believe",calender:"calendar",cemetary:"cemetery",collegue:"colleague",commited:"committed",concious:"conscious",definitly:"definitely",dissapoint:"disappoint",embarass:"embarrass",enviroment:"environment",existance:"existence",flourescent:"fluorescent",foriegn:"foreign",gaurd:"guard",garantee:"guarantee",goverment:"government",independant:"independent",knowlege:"knowledge",liason:"liaison",maintainance:"maintenance",neccessary:"necessary",noticable:"noticeable",occassion:"occasion",peice:"piece",priviledge:"privilege",publically:"publicly",recieved:"received",refered:"referred",succesful:"successful",tommorow:"tomorrow",wether:"whether",writting:"writing"};
  // Pulled out of runProofCheck (below) as a pure function of (kind,text) —
  // runProofCheck itself still only ever checks the main compose editor's
  // current step (quillRef/activeStep), same as before; MailMergeFullPreview
  // has its OWN, separate Quill instance (its own edits never touch
  // quillRef/activeStep), so its own "Spell Check"/"Grammar" ribbon buttons
  // call this same helper directly against ITS OWN current text instead of
  // reusing runProofCheck itself, which would otherwise silently proof-check
  // stale content in the wrong editor. Same typo dictionary, same rules,
  // just callable against whichever editor's text the caller actually has.
  const checkProofIssues=(kind,rawText)=>{
    const text=(rawText||"").toLowerCase();
    const words=text.match(/[a-z']+/g)||[];
    if(kind==="spell"){
      const found=new Set();
      words.forEach(w=>{if(COMMON_TYPOS[w])found.add(w+" → "+COMMON_TYPOS[w]);});
      return found.size===0
        ?{ok:true,message:"Spell check: no common misspellings found"}
        :{ok:false,message:`Spell check found: ${Array.from(found).slice(0,5).join(", ")}`};
    }
    const issues=[];
    if(/\b(\w+)\s+\1\b/.test(text))issues.push("repeated word");
    if(/  +/.test(text))issues.push("double space");
    if(/[a-z]\.[a-z]/i.test(text.replace(/\{\{[^}]*\}\}/g,"")))issues.push("missing space after period");
    if(/!!|\?\?/.test(text))issues.push("doubled punctuation");
    return issues.length===0
      ?{ok:true,message:"Grammar check: no issues found"}
      :{ok:false,message:"Grammar check found: "+issues.join(", ")};
  };
  const runProofCheck=(kind)=>{
    const q=quillRef.current?.getEditor?.();
    const text=(q?.getText()||"")+" "+(drafts[activeStep]?.subject||"");
    const{ok,message}=checkProofIssues(kind,text);
    addToast(message,ok?"success":"error");
  };

  useEffect(()=>{
    const q=quillRef.current?.getEditor?.();
    if(!q)return;
    const update=()=>{
      const text=q.getText().trim();
      const words=text?text.split(/\s+/).filter(Boolean).length:0;
      const chars=text.length;
      const readTime=Math.max(1,Math.round(words/200))+" min read";
      setWordCount({words,chars,readTime});
    };
    q.on("text-change",update);
    return()=>q.off("text-change",update);
  },[activeStep]);

  // Find/Replace logic now lives in the shared <FindReplaceDialog/>
  // component (richtext/FindReplaceDialog.js) so SequenceBuilder.js can
  // reuse the exact same, already-working implementation instead of a
  // second hand-rolled copy — see the <FindReplaceDialog/> usage below.

  const pasteAs=(mode)=>{
    const q=quillRef.current?.getEditor?.();
    if(!q){setShowPasteOptions(false);return;}
    setShowPasteOptions(false);
    const sel=q.getSelection(true);
    const idx=sel?sel.index:q.getLength();
    try{
      navigator.clipboard.readText().then(text=>{
        if(!text)return;
        if(mode==="text"){
          q.insertText(idx,text,"user");
        }else{
          q.clipboard.dangerouslyPasteHTML(idx,"<p>"+text.replace(/\n\n/g,"</p><p>").replace(/\n/g,"<br/>")+"</p>","user");
        }
        addToast("Pasted","success");
      }).catch(()=>addToast("Paste with Ctrl+V directly in the editor","info"));
    }catch(e){addToast("Paste with Ctrl+V directly in the editor","info");}
  };

  const preview=previewMode&&filteredRecipients[previewIdx];
  const activeFU=FOLLOW_UPS.find(f=>f.id===activeStep);
  const previewSubject=preview?applyMerge(drafts[activeStep].subject,filteredRecipients[previewIdx]):"";
  const previewBody=preview?applyMerge(applyRule(drafts[activeStep].body,filteredRecipients[previewIdx]),filteredRecipients[previewIdx]):"";

  const RIBBON_TABS=[
    {id:"home",label:"Home"},
    {id:"mailings",label:"Mailings"},
    {id:"insert",label:"Insert"},
    {id:"format",label:"Format Text"},
    {id:"review",label:"Review"},
    {id:"view",label:"View"},
    {id:"options",label:"Options"},
  ];

  // Stage-rail status map — see MailMergeStageRail's header comment for what
  // each signal actually means. Recomputed every render from real state
  // (cheap: a handful of booleans/lengths), not memoized, since none of the
  // ribbon tabs below re-render at a rate where that would matter.
  const hasRecipients=recipients.length>0;
  const hasMessageContent=FOLLOW_UPS.some(f=>drafts[f.id]?.body);
  const hasSent=!!mmRun||mergeStats.sent>0;
  const isMonitoring=!!mmRun&&["sending","paused"].includes(mmRun.status);
  // Sequential, one-current-at-a-time (like a real stepper): walk the first
  // 7 stages in order, everything before the first not-yet-done one is
  // "done", that one stage is "current", everything after is "upcoming" —
  // avoids two stages both reading as "current" at once just because both
  // of their individual prerequisites happen to already be satisfied.
  // Monitor (8th) is handled separately since it isn't a one-shot
  // completion — it reflects whether a run is actively in flight.
  const doneFlags=[hasRecipients,hasRecipients,hasMessageContent,aiPersonalizationUsed,hasPreviewed,emailsValidated,hasSent];
  const firstNotDone=doneFlags.findIndex(d=>!d);
  const seqStatus=(i)=>firstNotDone===-1?"done":(i<firstNotDone?"done":(i===firstNotDone?"current":"upcoming"));
  const stageStatus={
    contacts: seqStatus(0),
    mapdata: seqStatus(1),
    message: seqStatus(2),
    ai: seqStatus(3),
    preview: seqStatus(4),
    validate: seqStatus(5),
    send: seqStatus(6),
    monitor: isMonitoring?"current":(hasSent?"done":"upcoming"),
  };
  // Clicking a stage focuses the existing control that stage is about —
  // never a second/duplicate way to perform the action itself.
  const handleStageClick=(id)=>{
    if(id==="contacts"){setRibbonTab("mailings");fileSelectRef.current?.focus();}
    else if(id==="mapdata"){if(!hasRecipients)importCSV();}
    else if(id==="message"){setRibbonTab("home");}
    else if(id==="ai"){setRibbonTab("home");if(!hasRecipients)addToast("Load recipients first","error");}
    else if(id==="preview"){setPreviewMode(true);}
    else if(id==="validate"){
      const invalid=filteredRecipients.filter(r=>!r.email?.includes("@"));
      addToast(invalid.length===0?` All ${filteredRecipients.length} emails valid`:`${invalid.length} invalid emails found`,invalid.length===0?"success":"error");
      if(invalid.length===0&&filteredRecipients.length>0)setEmailsValidated(true);
    }
    else if(id==="send"){setRibbonTab("mailings");}
    else if(id==="monitor"){if(mmRun)setShowRotation(true);else addToast("Nothing sending yet","info");}
  };

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100vh",background:"#f3f2f1",fontFamily:"'Segoe UI',Calibri,sans-serif",fontSize:13}}>
      <style>{"@media (max-width:768px){.responsive-grid{grid-template-columns:1fr!important}.hide-on-mobile{display:none!important}}"}</style>
      {showMailMergeEditor&&(
        <MailMergeEmailEditor
          isMobile={isMobile}
          closing={mailMergeEditorClosing}
          mergeTags={MERGE_TAGS}
          contactFile={selectedFile}
          recipients={filteredRecipients}
          settings={settings}
          senders={senders}
          contactFiles={contactFiles}
          fromEmail={fromEmail}
          setFromEmail={setFromEmail}
          selectedFile={selectedFile}
          loadFile={loadFile}
          manualEmails={manualEmails}
          setManualEmails={setManualEmails}
          addManualRecipients={addManualRecipients}
          openComposeWindow={openComposeWindow}
          showManualEntry={showManualEntry}
          setShowManualEntry={setShowManualEntry}
          importCSV={importCSV}
          onClose={closeMailMergeEditor}
          addToast={addToast}
        />
      )}
      {composeWindows.map((w,idx)=>(
        <ComposeWindow
          key={w.id}
          id={w.id}
          index={idx}
          senders={senders}
          defaultFrom={fromEmail}
          onClose={closeComposeWindow}
          onSend={sendComposeEmail}
          addToast={addToast}
        />
      ))}
      <style>{"@keyframes mmSlideIn{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}@keyframes mmSlideOut{from{transform:translateY(0);opacity:1}to{transform:translateY(24px);opacity:0}}@keyframes spin{to{transform:rotate(360deg)}}.mm-quill .ql-toolbar{background:#f8f7f6;border-color:#d1cec9!important}.mm-quill .ql-container{border-color:#d1cec9!important;background:#fff}.mm-quill .ql-editor{min-height:320px;font-family:Calibri,sans-serif;font-size:14px;line-height:1.4;color:#201f1e;padding:40px 60px}.mm-quill .ql-editor.ql-blank::before{left:60px;color:#a19f9d;font-style:normal}.mm-quill .ql-editor p{margin:0}.mm-quill .ql-editor ul{list-style-type:disc;padding-left:24px}.mm-quill .ql-editor ol{list-style-type:decimal;padding-left:24px}.mm-quill .ql-editor li{margin-bottom:4px}.mm-quill .ql-editor table{border-collapse:collapse;width:100%;margin:8px 0}.mm-quill .ql-editor table td,.mm-quill .ql-editor table th{border:1px solid #d1cec9;padding:6px 10px}.mm-quill .ql-editor img{max-width:100%;height:auto}.mm-quill .ql-editor a{color:#0078d4;text-decoration:underline}.mm-quill .ql-editor blockquote{border-left:4px solid #d1cec9;padding-left:16px;color:#605e5c;margin:8px 0}.mm-quill .ql-editor hr.mm-divider{border:none;border-top:1px solid #d1cec9;margin:12px 0}@media (max-width:768px){.responsive-grid{grid-template-columns:1fr!important}.hide-on-mobile{display:none!important}}"}</style>
      

      {/* Title bar with tabs */}
      <div style={{background:C.nav,padding:"0",display:"flex",flexDirection:"column",flexShrink:0,userSelect:"none"}}>
        {/* Top bar */}
        <div style={{padding:"4px 0 4px 12px",display:"flex",alignItems:"center",gap:8,minHeight:32}}>
        {/* Quick access toolbar */}
        <div style={{display:"flex",alignItems:"center",gap:2}}>
          <span style={{fontSize:14,display:"flex"}}><RIcon name="mailNew" size={14} color="#fff"/></span>
          {[{ic:<RIcon name="save" size={12} color="#fff"/>,tt:"Save",fn:()=>addToast("Saved","success")},{ic:<>&#8630;</>,tt:"Undo",fn:()=>quillRef.current?.getEditor?.()?.history.undo()},{ic:<>&#8631;</>,tt:"Redo",fn:()=>quillRef.current?.getEditor?.()?.history.redo()}].map((b,i)=>(
            <button key={i} title={b.tt} onClick={b.fn}
              style={{background:"rgba(255,255,255,.1)",border:"none",color:"#fff",borderRadius:2,padding:"2px 6px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center"}}>
              {b.ic}
            </button>
          ))}
        </div>
        <span style={{flex:1}}/>
        {/* Stats */}
        <div style={{display:"flex",gap:10,alignItems:"center",fontSize:11,color:"rgba(255,255,255,.75)"}}>
          <span>Words: {wordCount.words}</span>
          <span>|</span>
          <span>{wordCount.readTime}</span>
          {recipients.length>0&&<><span>|</span><span>{filteredRecipients.length} recipients</span></>}
        </div>
        <button onClick={()=>setShowFindReplace(true)} title="Find & Replace"
          style={{background:"rgba(255,255,255,.1)",border:"none",color:"#fff",borderRadius:2,padding:"3px 8px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center"}}><RIcon name="search" size={13} color="#fff"/></button>
        <button onClick={()=>setShowWordOptions(true)} title="Options"
          style={{background:"rgba(255,255,255,.1)",border:"none",color:"#fff",borderRadius:2,padding:"3px 8px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center"}}><RIcon name="gear" size={13} color="#fff"/></button>
        {/* Window controls. There used to be a custom "Minimize" button here
            that called window.resizeTo(800,600) — that's a real API, but all
            it does is shrink the window's pixel dimensions in place; it
            can't move it to the taskbar or hide it (no browser exposes a
            script-triggerable "minimize", on this or any site — it's an
            OS-level action only the user's own click on the REAL native
            title bar can trigger). That's what produced the "shrinks and
            just sits there" behavior. Since this window is a genuine
            window.open() popup (see App.js), it already has a real native
            title bar with a real, working minimize button drawn by the
            browser itself, above this custom bar — that already does the
            real, correct thing for free, so the broken custom one was
            removed rather than left doing the wrong thing. Maximize (below)
            stays: the Fullscreen API it calls is a real, different, and
            fully legitimate capability (edge-to-edge, no browser chrome at
            all) that script genuinely can trigger. */}
        <div style={{display:"flex",alignItems:"stretch",marginLeft:8,flexShrink:0}}>
          <button onClick={()=>{if(document.fullscreenElement){document.exitFullscreen();setChromeMode("windowed");}else{document.documentElement.requestFullscreen();setChromeMode("full");}}} title="Maximize"
            style={{width:46,height:32,background:"transparent",border:"none",color:"#fff",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.15)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            &#9634;
          </button>
          <button onClick={()=>window.close()} title="Close"
            style={{width:46,height:32,background:"transparent",border:"none",color:"#fff",cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}
            onMouseEnter={e=>e.currentTarget.style.background="#c42b1c"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            &#10005;
          </button>
        </div>
        </div>
        {/* Email step tabs — the window-chrome-style document tabs above the
            ribbon. Hidden while chromeMode is "full" (the default on open,
            matching this window opening full screen); revealed once the
            user clicks Minimize (or otherwise leaves full screen — see the
            fullscreenchange listener above), matching a real desktop app's
            document tabs only showing once you're out of full-screen. */}
        {chromeMode==="windowed"&&(
        <div style={{display:"flex",alignItems:"flex-end",padding:"0 8px",overflowX:"auto",background:"rgba(0,0,0,.15)"}}>
          {FOLLOW_UPS.map(fu=>{
            const isOpen=openTabs.includes(fu.id);
            const isActive=activeStep===fu.id&&isOpen;
            return(
              <div key={fu.id} onClick={()=>{if(!openTabs.includes(fu.id))setOpenTabs(p=>[...p,fu.id]);setActiveStep(fu.id);setMinimized(p=>({...p,[fu.id]:false}));}}
                style={{display:"flex",alignItems:"center",gap:5,padding:"6px 11px",background:isActive?"#fff":isOpen?"rgba(255,255,255,.2)":"rgba(255,255,255,.05)",borderRadius:WR.md+"px "+WR.md+"px 0 0",cursor:"pointer",marginRight:2,minWidth:110,flexShrink:0,borderTop:isActive?"2.5px solid "+fu.color:"2.5px solid transparent",boxShadow:isActive?"0 -2px 8px rgba(0,0,0,.08)":"none",transition:"all .12s"}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:fu.color,flexShrink:0}}/>
                <span style={{fontSize:11,fontWeight:isActive?600:400,color:isActive?C.text:"rgba(255,255,255,.85)",flex:1,whiteSpace:"nowrap"}}>{fu.label}</span>
                {isOpen&&(
                  <div style={{display:"flex",gap:1}}>
                    <button onClick={e=>{e.stopPropagation();setMinimized(p=>({...p,[fu.id]:!p[fu.id]}));}} style={{width:11,height:11,borderRadius:"50%",border:"none",background:"#fbbf24",cursor:"pointer",fontSize:7,display:"flex",alignItems:"center",justifyContent:"center"}}></button>
                    <button onClick={e=>{e.stopPropagation();setMaximized(p=>({...p,[fu.id]:!p[fu.id]}));}} style={{width:11,height:11,borderRadius:"50%",border:"none",background:"#34d399",cursor:"pointer",fontSize:7,display:"flex",alignItems:"center",justifyContent:"center"}}></button>
                    <button onClick={e=>{e.stopPropagation();const t=openTabs.filter(x=>x!==fu.id);setOpenTabs(t);if(activeStep===fu.id)setActiveStep(t[t.length-1]||"initial");}} style={{width:11,height:11,borderRadius:"50%",border:"none",background:"#f87171",cursor:"pointer",fontSize:7,display:"flex",alignItems:"center",justifyContent:"center"}}></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Ribbon tabs — now the same shared <RibbonTabButton> OutlookWorkspace.js
          uses (rounded tops, lift-on-active shadow) instead of a hand-rolled
          flat-underline button, so Mail Merge's tab strip matches Workspace's
          rounded/professional ribbon pass exactly rather than approximating
          it. Sits on C.sidebar (the same light-gray tray Workspace's tab bar
          uses) so the rounded white ribbon panel below reads as a floating
          card. */}
      <div style={{background:C.sidebar,borderBottom:"1px solid "+C.ribbonBorder,display:"flex",padding:"4px 8px 0",flexShrink:0}}>
        {RIBBON_TABS.map(t=>(
          <RibbonTabButton key={t.id} label={t.label} isActive={ribbonTab===t.id} onClick={()=>setRibbonTab(t.id)}/>
        ))}
      </div>

      {/* Ribbon content — a floating white rounded card (rounded/professional
          ribbon pass), replacing the flat full-bleed strip. A previous pass
          here widened maxHeight from 68 to 112 to stop clipping — but the
          Mailings tab's "Preview Results" group (nav row + recipient count
          + Preview Results button + Find Recipient link + Check for Errors
          button, 5 stacked rows) is genuinely taller than 112px, so it was
          STILL being cut off (Check for Errors half-hidden) — a real,
          user-visible regression, not a one-off. Root cause: any maxHeight
          on a fixed-height ribbon strip is fighting against content that
          legitimately varies in height per tab/group; capping it will
          always eventually clip some future (or existing) group. Fix:
          no maxHeight at all — minHeight keeps sparse tabs from looking too
          short, and the flex row (alignItems:"stretch", so every group's
          divider still spans the full height) simply grows to fit whichever
          group is tallest on the active tab, which is what a "card that
          contains a ribbon's worth of content" should actually do. Kept
          overflowX:"auto" for many groups on a narrow window; overflowY
          left at its default (visible) — with no maxHeight there is
          nothing for it to ever clip. The two dropdown menus this panel
          hosts (Schedule, Categorize) render through <RibbonMenu>'s
          document.body portal rather than as plain position:absolute
          children, so they were never affected by this clipping. */}
      <div style={{background:"#fff",border:"1px solid "+C.ribbonBorder,borderRadius:WR.lg,boxShadow:"0 2px 10px rgba(0,0,0,.06)",margin:"8px 10px",display:"flex",alignItems:"stretch",padding:"6px 8px 4px",flexShrink:0,minHeight:78,overflowX:"auto"}}>

        {ribbonTab==="home"&&(
          <>
            <RGroup label="Clipboard">
              {/* Kept as this file's own pre-existing Paste Options popup
                  (Keep Source / Merge / Text Only via pasteAs()) rather than
                  WordRibbon's generic clipboard-API fallback — this one
                  already works and is more capable, so showClipboard is off
                  below to avoid a second, weaker Paste Special control. */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"2px 4px"}}>
                <button onClick={()=>{const el=document.querySelector(".mm-quill .ql-editor");if(el){const r=el.getBoundingClientRect();setPastePos({x:r.left+10,y:r.top+10});}setShowPasteOptions(true);}}
                  style={{width:36,height:36,background:"#f3f2f1",border:"1px solid #d1cec9",borderRadius:3,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>&#9636;</button>
                <div style={{display:"flex",gap:2}}>
                  <button onClick={()=>{const q=quillRef.current?.getEditor?.();const r=q?.getSelection();if(r){navigator.clipboard.writeText(q.getText(r.index,r.length));q.deleteText(r.index,r.length);}}} title="Cut"
                    style={{padding:"2px 5px",border:"1px solid #d1cec9",borderRadius:2,background:"#fff",cursor:"pointer",fontSize:11}}>&#9986;</button>
                  <button onClick={()=>{const q=quillRef.current?.getEditor?.();const r=q?.getSelection();if(r)navigator.clipboard.writeText(q.getText(r.index,r.length));}} title="Copy"
                    style={{padding:"2px 5px",border:"1px solid #d1cec9",borderRadius:2,background:"#fff",cursor:"pointer",fontSize:11}}>&#9636;</button>
                  <button onClick={()=>addToast("Format Painter","info")} title="Format Painter"
                    style={{padding:"2px 5px",border:"1px solid #d1cec9",borderRadius:2,background:"#fff",cursor:"pointer",fontSize:11}}>&#9998;</button>
                </div>
                <span style={{fontSize:9,color:"#a19f9d"}}>Paste</span>
              </div>
            </RGroup>
            <WordRibbon getQuill={getQuill} showClipboard={false} showStyles formatMarksOn={formatMarksOn} onToggleFormatMarks={()=>setFormatMarksOn(v=>!v)} addToast={addToast} docFilename="mail-merge-message"
              onImported={(name)=>{
                // A .docx import only ever fills the BODY (see WordRibbon's
                // own comment) — auto-filling the subject from the file
                // name when it's still blank means a straight-to-Send
                // attempt right after importing isn't blocked by a missing
                // subject the import itself had no way to set. Never
                // overwrites a subject the user already typed.
                setDrafts(p=>(p[activeStep]&&!p[activeStep].subject?.trim())?{...p,[activeStep]:{...p[activeStep],subject:name}}:p);
              }}/>
            <RGroup label="Editing">
              <RBtn icon={<RIcon name="search" size={14} color={C.text}/>} label="Find" large onClick={()=>setShowFindReplace(true)}/>
              <RBtn icon={<>&#8644;</>} label="Replace" large onClick={()=>setShowFindReplace(true)}/>
              <RBtn icon={<>&#9745;</>} label="Select All" large onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.setSelection(0,q.getLength());}}/>
            </RGroup>
            <RGroup label="AI">
              <RBtn icon={<RIcon name="sparkle" size={16} color={C.accent}/>} label="AI Draft" large onClick={aiGenerateStep} disabled={aiLoading} active/>
              <RBtn icon={<RIcon name="sparkle" size={16} color={C.text}/>} label="All Steps" large onClick={aiGenerateAll} disabled={aiLoading}/>
            </RGroup>
          </>
        )}

        {ribbonTab==="mailings"&&(
          <>
            <RGroup label="Start Mail Merge">
              {/* Templates used to also have its own small link button here
                  — now that it lives as a proper row in Write & Insert
                  Fields, keeping a second copy here just duplicated it
                  (same handler, same label, right next to it visually).
                  Removed; Email Messages is this group's one control. */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"2px 4px",cursor:"pointer",borderRadius:WR.sm,background:"#deecf9",border:"1px solid #0078d422",minWidth:58}} onClick={()=>setShowMailMergeEditor(true)}>
                <div style={{width:32,height:32,background:"#0078d4",borderRadius:WR.sm,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}><RIcon name="mailNew" size={17} color="#fff"/></div>
                <span style={{fontSize:9,color:"#0078d4",fontWeight:600,textAlign:"center",lineHeight:1.2}}>Email<br/>Messages</span>
              </div>
            </RGroup>

            <RGroup label="Select Recipients">
              {/* Subtitles (e.g. "CSV / Excel / XLSX") were their own text
                  line under each button's title — that's what made this
                  the ribbon's tallest group. Moved to a title= tooltip
                  instead (same info, still discoverable on hover) so each
                  row is a single compact line; handlers/labels unchanged. */}
              <div style={{display:"flex",flexDirection:"column",gap:1}}>
                <button onClick={importCSV} title="CSV / Excel / XLSX" style={{display:"flex",alignItems:"center",gap:6,padding:"3px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,borderRadius:2,fontFamily:"'Segoe UI',sans-serif",fontWeight:500}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16,display:"flex"}}><RIcon name="upload" size={15} color={C.textSub}/></span>
                  <span style={{fontSize:11}}>Upload File</span>
                </button>
                <button onClick={()=>fileSelectRef.current?.focus()} title="From contacts" style={{display:"flex",alignItems:"center",gap:6,padding:"3px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16,display:"flex"}}><RIcon name="people" size={15} color={C.textSub}/></span>
                  <span style={{fontSize:11}}>Use Existing List</span>
                </button>
                <button onClick={()=>recipientSearchRef.current?.focus()} title="Filter & sort" style={{display:"flex",alignItems:"center",gap:6,padding:"3px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16,display:"flex"}}><RIcon name="textLines" size={15} color={C.textSub}/></span>
                  <span style={{fontSize:11}}>Edit Recipient List</span>
                </button>
              </div>
            </RGroup>

            <RGroup label="Write & Insert Fields">
              {/* "Highlight Merge Fields" (the previewMode toggle) removed
                  from here per request — previewMode is still fully live
                  and reachable the same way it always was elsewhere (the
                  step-progress bar's "Preview" stage, and clicking a
                  recipient row both call setPreviewMode(true) already;
                  see handleStageClick above), so no functionality was lost,
                  just this one entry point. Templates (same openTemplateLib
                  handler as the Start Mail Merge group's own Templates
                  button) takes its slot instead — quicker access to
                  templates right where fields are being inserted. Every
                  row here now shares one icon+label pattern (fixed
                  15px-square flex-centered icon slot, then the label) so
                  symbols line up with their text consistently. Text/icon
                  color is C.textSub (a medium "light black" gray, #514F4D)
                  at a normal-not-bold weight rather than C.text's near-
                  black #16161A at 600 — was reading as too heavy/dark;
                  no more accent-blue text on open/active states either,
                  only a background tint. */}
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <button onClick={()=>openTemplateLib()} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{width:15,height:15,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><RIcon name="doc" size={14} color={C.textSub}/></span>
                  <span style={{fontSize:11}}>Templates</span>
                </button>
                <div style={{display:"flex",gap:2}}>
                  <button onClick={()=>insertMergeTag("{{FirstName}} {{LastName}}\n{{Company}}")} title="Address Block" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"3px 6px",border:"none",background:"transparent",cursor:"pointer",fontSize:10,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif",whiteSpace:"nowrap"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{width:13,height:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><RIcon name="idCard" size={13} color={C.textSub}/></span>
                    <span>Address</span>
                  </button>
                  <button onClick={()=>insertMergeTag("Hi {{FirstName}},")} title="Greeting Line" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"3px 6px",border:"none",background:"transparent",cursor:"pointer",fontSize:10,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif",whiteSpace:"nowrap"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{width:13,height:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:13,lineHeight:1}}>&#9993;</span>
                    <span>Greeting</span>
                  </button>
                </div>
                {/* Insert Merge Field — a real dropdown listing every
                    personalization variable the merge engine
                    (mergeTags.js) actually supports, not just a 4-tag
                    sample. "Other Fields" only appears when the loaded
                    recipients carry real data beyond the standard set
                    (e.g. phone/linkedin from "Use Existing List") — see
                    otherMergeFields' definition above for why CSV imports
                    never populate it. */}
                <RibbonDropdownButton minWidth={190}
                  openState={[showMergeFieldMenu,setShowMergeFieldMenu]}
                  trigger={({toggle,open})=>(
                    <button onClick={toggle}
                      style={{display:"flex",alignItems:"center",gap:6,width:"100%",padding:"4px 10px",border:"none",background:open?"#deecf9":"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}}
                      onMouseEnter={e=>{if(!open)e.currentTarget.style.background="#e1dfdd";}} onMouseLeave={e=>{if(!open)e.currentTarget.style.background="transparent";}}>
                      <span style={{width:15,height:15,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><RIcon name="tag" size={14} color={C.textSub}/></span>
                      <span style={{fontSize:11,flex:1,textAlign:"left"}}>Insert Merge Field</span>
                      <span style={{fontSize:8,color:C.textSub}}>&#9662;</span>
                    </button>
                  )}>
                  {(close)=>(<>
                    <div style={{padding:"4px 8px 2px",fontSize:9,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:".3px"}}>Standard Fields</div>
                    {MERGE_TAGS.map(tag=>(
                      <button key={tag} onMouseDown={e=>{e.preventDefault();insertMergeTag(tag);close();}}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 10px",fontSize:12,color:C.text,cursor:"pointer",borderRadius:WR.sm,fontFamily:"'Segoe UI',sans-serif"}}
                        onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span>{MERGE_TAG_LABELS[tag]||tag}</span>
                        <span style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>{tag}</span>
                      </button>
                    ))}
                    {otherMergeFields.length>0&&(
                      <>
                        <div style={{height:1,background:C.ribbonBorder,margin:"4px 2px"}}/>
                        <div style={{padding:"4px 8px 2px",fontSize:9,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:".3px"}}>Other Fields</div>
                        {otherMergeFields.map(key=>(
                          <button key={key} onMouseDown={e=>{e.preventDefault();insertMergeTag("{{"+key+"}}");close();}}
                            style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 10px",fontSize:12,color:C.text,cursor:"pointer",borderRadius:WR.sm,fontFamily:"'Segoe UI',sans-serif"}}
                            onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <span style={{textTransform:"capitalize"}}>{key}</span>
                            <span style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>{"{{"+key+"}}"}</span>
                          </button>
                        ))}
                      </>
                    )}
                    <div style={{height:1,background:C.ribbonBorder,margin:"4px 2px"}}/>
                    <button onMouseDown={e=>{e.preventDefault();setShowRules(true);close();}}
                      style={{display:"flex",alignItems:"center",gap:8,width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 10px",fontSize:12,color:C.purple,cursor:"pointer",borderRadius:WR.sm,fontFamily:"'Segoe UI',sans-serif"}}
                      onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <RIcon name="gear" size={13} color={C.purple}/> Insert Rule (IF/THEN/ELSE)…
                    </button>
                  </>)}
                </RibbonDropdownButton>
              </div>
            </RGroup>

            <RGroup label="Rules">
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <button onClick={()=>setShowRules(!showRules)} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16,display:"flex"}}><RIcon name="gear" size={15} color={C.textSub}/></span>
                  <div><div style={{fontSize:11}}>Rules</div><div style={{fontSize:9,color:C.textSub}}>IF/THEN/ELSE</div></div>
                </button>
                {/* Used to just toast the current recipients' already-
                    normalized keys read-only ("Detected columns: id, name,
                    first...") — informational only, no way to fix a
                    misdetected column. Now reopens the real mapping grid on
                    the last-imported file's raw columns so a column can
                    actually be remapped and reimported. Only meaningful for
                    file imports — "Use Existing List" and manual-paste
                    recipients were never CSV columns to begin with. */}
                <button onClick={()=>{
                  if(importRawRows.length){setShowMapFields(true);return;}
                  if(!recipients.length){addToast("Load a recipient list first","error");return;}
                  addToast("Field mapping applies to CSV/Excel imports — this list wasn't loaded from a file.","info");
                }} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16}}>&#8644;</span>
                  <div><div style={{fontSize:11}}>Match Fields</div></div>
                </button>
                {/* "Update Labels" (mail-merge printed label sheets) removed — no
                    equivalent concept for an email-only tool. */}
              </div>
            </RGroup>

            <RGroup label="Preview Results">
              <div style={{display:"flex",flexDirection:"column",gap:2,alignItems:"center",padding:"2px 4px"}}>
                {/* Nav controls, recipient count, and "Find Recipient" all
                    condensed onto one row (were 3 separate stacked rows) —
                    same controls, same handlers, just laid out horizontally
                    instead of vertically so this group doesn't dictate the
                    whole ribbon's height the way it did before. */}
                <div style={{display:"flex",alignItems:"center",gap:2}}>
                  <button onClick={()=>setPreviewIdx(0)} disabled={!previewMode} title="First" style={{background:"none",border:"1px solid #d1cec9",borderRadius:2,padding:"1px 4px",cursor:previewMode?"pointer":"not-allowed",fontSize:10,opacity:previewMode?1:.4}}>&#8676;</button>
                  <button onClick={()=>setPreviewIdx(p=>Math.max(0,p-1))} disabled={!previewMode} title="Previous" style={{background:"none",border:"1px solid #d1cec9",borderRadius:2,padding:"1px 4px",cursor:previewMode?"pointer":"not-allowed",fontSize:10,opacity:previewMode?1:.4}}>&#9664;</button>
                  <input value={previewMode?previewIdx+1:""} onChange={e=>setPreviewIdx(Math.max(0,Math.min(filteredRecipients.length-1,(parseInt(e.target.value)||1)-1)))}
                    disabled={!previewMode} placeholder=""
                    style={{width:26,textAlign:"center",border:"1px solid #d1cec9",borderRadius:2,padding:"1px",fontSize:10,color:C.text,background:previewMode?"#fff":"#f3f2f1"}}/>
                  <button onClick={()=>setPreviewIdx(p=>Math.min(filteredRecipients.length-1,p+1))} disabled={!previewMode} title="Next" style={{background:"none",border:"1px solid #d1cec9",borderRadius:2,padding:"1px 4px",cursor:previewMode?"pointer":"not-allowed",fontSize:10,opacity:previewMode?1:.4}}>&#9654;</button>
                  <button onClick={()=>setPreviewIdx(filteredRecipients.length-1)} disabled={!previewMode} title="Last" style={{background:"none",border:"1px solid #d1cec9",borderRadius:2,padding:"1px 4px",cursor:previewMode?"pointer":"not-allowed",fontSize:10,opacity:previewMode?1:.4}}>&#8677;</button>
                  <span style={{fontSize:9,color:C.textMuted,marginLeft:2,whiteSpace:"nowrap"}}>of {filteredRecipients.length}</span>
                  <button onClick={()=>recipientSearchRef.current?.focus()} title="Find Recipient"
                    style={{background:"transparent",border:"none",color:C.accent,fontSize:12,cursor:"pointer",padding:"0 2px",display:"flex",alignItems:"center",marginLeft:1}}>
                    <RIcon name="search" size={11} color={C.accent}/>
                  </button>
                </div>
                {/* Was setPreviewMode(!previewMode) — toggled the SAME
                    cramped compose column into a read-only inline render.
                    Now opens the dedicated full-window preview workspace
                    instead (MailMergeFullPreview.js), matching Word's own
                    Print Preview taking over the window rather than
                    shrinking into a corner of it. previewMode/previewIdx are
                    untouched by this — previewIdx is only read here, as the
                    seed the full-window view opens on. */}
                <button onClick={()=>setShowFullPreview(true)}
                  style={{background:"#f3f2f1",color:C.textSub,border:"1px solid #d1cec9",borderRadius:WR.sm,padding:"2px 12px",fontSize:10,cursor:"pointer",fontWeight:400,fontFamily:"'Segoe UI',sans-serif",width:"100%"}}>
                  &#9673; Preview Results
                </button>
                {/* Check for Errors — same two checks the Review tab's
                    "Check Errors"/"Validate Emails" buttons already run
                    (see ribbonTab==="review" below), just combined into one
                    button here since Word's own Mailings tab surfaces
                    "Check for Errors" right in Preview Results rather than
                    only on a separate Review tab. No new validation logic —
                    identical checks, just also reachable from where you're
                    already working. */}
                <button onClick={()=>{
                  const missing=FOLLOW_UPS.filter(f=>!drafts[f.id].subject||!drafts[f.id].body);
                  const invalid=filteredRecipients.filter(r=>!r.email?.includes("@"));
                  if(missing.length===0&&invalid.length===0){
                    addToast(" No errors found — ready to merge!","success");
                    if(filteredRecipients.length>0)setEmailsValidated(true);
                    return;
                  }
                  const parts=[];
                  if(missing.length)parts.push(`Missing content: ${missing.map(f=>f.label).join(", ")}`);
                  if(invalid.length)parts.push(`${invalid.length} invalid email(s)`);
                  addToast(parts.join(" · "),"error");
                }}
                  style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,background:"#fff4ce",color:"#8a6d00",border:"1px solid #d83b0133",borderRadius:WR.sm,padding:"2px 12px",fontSize:10,cursor:"pointer",fontWeight:600,fontFamily:"'Segoe UI',sans-serif",width:"100%"}}>
                  <RIcon name="infoCircle" size={11} color="#8a6d00"/> Check for Errors
                </button>
              </div>
            </RGroup>

            <RGroup label="Finish">
              {/* Was the ribbon's tallest group after Preview Results/
                  Select Recipients/Write & Insert Fields were compacted —
                  same 3 rows, just trimmed padding/gap (6px/14px→4px/12px
                  on the main button, row gap 3→2) so it no longer sets the
                  ribbon's overall height. Same handlers/labels throughout. */}
              <div style={{display:"flex",flexDirection:"column",gap:2,padding:"2px 2px"}}>
                <button onClick={finishSend} disabled={sending}
                  style={{background:C.accent,color:"#fff",border:"none",borderRadius:WR.md,padding:"4px 12px",fontSize:12,cursor:"pointer",fontWeight:700,fontFamily:"'Segoe UI',sans-serif",display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap",boxShadow:"0 2px 6px rgba(0,0,0,.12)"}}>
                  <RIcon name="send" size={14} color="#fff"/> Finish & Merge
                </button>
                <div style={{display:"flex",gap:2}}>
                  <button onClick={finishSend} disabled={sending} style={{flex:1,background:"#dff6dd",color:C.green,border:"1px solid #107c1033",borderRadius:WR.sm,padding:"3px 6px",fontSize:10,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:600}}>&#9654; Send Now</button>
                  <div style={{flex:1}}>
                    <RibbonDropdownButton minWidth={190}
                      openState={[showScheduleMenu,setShowScheduleMenu]}
                      trigger={({toggle})=>(
                        <button onClick={toggle} style={{width:"100%",background:"#fff4ce",color:C.amber,border:"1px solid #d83b0133",borderRadius:WR.sm,padding:"3px 6px",fontSize:10,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:600}}>&#9200; Schedule</button>
                      )}>
                      <div style={{padding:4,display:"flex",flexDirection:"column",gap:6}}>
                        <label style={{fontSize:10,color:C.textSub}}>Send batch at:</label>
                        <input type="datetime-local" value={scheduleAt} onChange={e=>setScheduleAt(e.target.value)}
                          style={{border:"1px solid "+C.ribbonBorder,borderRadius:WR.sm,padding:"4px 6px",fontSize:11}}/>
                        <button onClick={()=>scheduleMergeSend(scheduleAt)} disabled={scheduling||!scheduleAt}
                          style={{background:C.accent,color:"#fff",border:"none",borderRadius:WR.sm,padding:"5px 8px",fontSize:11,cursor:scheduling?"not-allowed":"pointer",fontWeight:600}}>
                          {scheduling?"Scheduling...":"Confirm"}
                        </button>
                      </div>
                    </RibbonDropdownButton>
                  </div>
                </div>
                <div style={{display:"flex",gap:2}}>
                  <button onClick={assignToSequence} style={{flex:1,background:"#f4f0ff",color:C.purple,border:"1px solid #5c2d9133",borderRadius:WR.sm,padding:"3px 6px",fontSize:10,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:600}}>&#8635; Sequence</button>
                  <button onClick={saveDraftReal} disabled={savingDraft} style={{flex:1,background:"#f3f2f1",color:C.textSub,border:"1px solid #d1cec9",borderRadius:WR.sm,padding:"3px 6px",fontSize:10,cursor:savingDraft?"not-allowed":"pointer",fontFamily:"'Segoe UI',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><RIcon name="save" size={11} color={C.textSub}/> {savingDraft?"Saving...":"Draft"}</button>
                </div>
              </div>
            </RGroup>

            <RGroup label="Rotation & Testing">
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"2px 4px",cursor:"pointer",borderRadius:WR.sm,background:mmRun?.enabled||showRotation?"#f4f0ff":"transparent",minWidth:70}} onClick={openRotationPanel}>
                <div style={{width:32,height:32,background:C.purple,borderRadius:WR.sm,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:"#fff"}}>⟳</div>
                <span style={{fontSize:9,color:C.purple,fontWeight:600,textAlign:"center",lineHeight:1.2}}>Rotate &<br/>Test</span>
              </div>
            </RGroup>

            {/* Proofing — the same Find & Replace / Options actions that
                already live as small icon buttons up in the title bar,
                also surfaced here as a proper labeled ribbon group (this is
                exactly where Word's own ribbon puts Proofing, on the
                Mailings/Home tabs). Same handlers, same state — purely an
                additional entry point, fills what was dead space to the
                right of Rotation & Testing. */}
            <RGroup label="Proofing">
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <button onClick={()=>setShowFindReplace(true)} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16,display:"flex"}}><RIcon name="search" size={15} color={C.textSub}/></span>
                  <div style={{textAlign:"left"}}><div style={{fontSize:11}}>Find & Replace</div></div>
                </button>
                <button onClick={()=>setShowWordOptions(true)} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,color:C.textSub,fontWeight:500,borderRadius:2,fontFamily:"'Segoe UI',sans-serif"}} onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:16,display:"flex"}}><RIcon name="gear" size={15} color={C.textSub}/></span>
                  <div style={{textAlign:"left"}}><div style={{fontSize:11}}>Options</div></div>
                </button>
              </div>
            </RGroup>

            {/* Merge Summary — same three figures already shown in the step
                editor's own left sidebar (Recipients / Templates filled /
                Total Emails), same expressions, just also surfaced as a
                glanceable ribbon readout — no new computation. */}
            <RGroup label="Merge Summary">
              <div style={{display:"flex",flexDirection:"column",gap:3,padding:"2px 6px",minWidth:96}}>
                {[
                  ["Recipients",filteredRecipients.length,C.accent],
                  ["Templates",FOLLOW_UPS.filter(f=>drafts[f.id]?.body).length+"/"+FOLLOW_UPS.length,C.green],
                  ["Total Emails",filteredRecipients.length*FOLLOW_UPS.filter(f=>drafts[f.id]?.body).length,C.purple],
                ].map(([l,v,c])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",gap:10}}>
                    <span style={{fontSize:10,color:C.textSub}}>{l}</span>
                    <span style={{fontSize:10,fontWeight:700,color:c,fontFamily:"monospace"}}>{v}</span>
                  </div>
                ))}
              </div>
            </RGroup>
          </>
        )}

        {ribbonTab==="message"&&(
          <>
            <RGroup label="Send">
              <RBtn icon="" label="Send All" large onClick={finishSend} disabled={sending} active/>
              <RBtn icon="" label="Save Draft" large onClick={saveDraftReal} disabled={savingDraft}/>
            </RGroup>
            <RGroup label="Basic Actions">
              <RBtn icon="" label="Undo" onClick={()=>quillRef.current?.getEditor?.().history.undo()}/>
              <RBtn icon="" label="Redo" onClick={()=>quillRef.current?.getEditor?.().history.redo()}/>
              <RBtn icon="" label="Save Template" onClick={()=>saveTemplate()}/>
              <RBtn icon="" label="Templates" onClick={()=>openTemplateLib()}/>
            </RGroup>
            <RGroup label="Tags">
              <RBtn icon="" label="Follow Up" active={!!stepFlags[activeStep]?.flagged} onClick={()=>toggleStepFlag("flagged")}/>
              <RibbonDropdownButton minWidth={110}
                openState={[showCategoryMenu,setShowCategoryMenu]}
                trigger={({toggle})=><RBtn icon="" label="Categorize" active={!!stepFlags[activeStep]?.category} onClick={toggle}/>}>
                    {Object.entries(CATEGORY_COLORS_MM).map(([key,color])=>(
                      <button key={key} onClick={()=>setStepCategory(key)}
                        style={{display:"flex",alignItems:"center",gap:6,width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm,textTransform:"capitalize"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{width:9,height:9,borderRadius:"50%",background:color,flexShrink:0}}/>{key}
                      </button>
                    ))}
                    <button onClick={()=>setStepCategory(null)}
                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.textSub,borderRadius:WR.sm}}
                      onMouseEnter={e=>e.currentTarget.style.background="#e1dfdd"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Clear</button>
              </RibbonDropdownButton>
              <RBtn icon="" label="Important" active={!!stepFlags[activeStep]?.important} onClick={()=>toggleStepFlag("important")}/>
              <RBtn icon="" label="Complete" active={!!stepFlags[activeStep]?.complete} onClick={()=>toggleStepFlag("complete")}/>
            </RGroup>
            <RGroup label="AI">
              <RBtn icon="" label="AI Generate" large onClick={aiGenerateStep} disabled={aiLoading} active/>
              <RBtn icon="" label="Generate All" large onClick={aiGenerateAll} disabled={aiLoading}/>
            </RGroup>
          </>
        )}

        {ribbonTab==="insert"&&(
          <>
            <RGroup label="Merge Fields">
              {MERGE_TAGS.map(tag=>(
                <RBtn key={tag} sm icon={tag.replace(/{{|}}/g,"").slice(0,4)} label={tag.replace(/{{|}}/g,"")} onClick={()=>insertMergeTag(tag)}/>
              ))}
            </RGroup>
            <RGroup label="Blocks">
              <RBtn icon={<>&#9993;</>} label="Greeting" large onClick={()=>insertMergeTag("Hi {{FirstName}},")}/>
              <RBtn icon={<RIcon name="idCard" size={16} color={C.text}/>} label="Address Block" large onClick={()=>insertMergeTag("{{FirstName}} {{LastName}}\n{{Title}}\n{{Company}}")}/>
              <RBtn icon={<>&#9997;</>} label="Signature" large onClick={()=>{const q=quillRef.current?.getEditor?.();if(q){const idx=q.getLength();q.insertText(idx,`\n\nBest regards,\n${settings.sdrName||"Anji"}\n${settings.company||"SDR ReachOut"}`);};}}/>
            </RGroup>
            <RGroup label="Rules">
              <RBtn icon={<RIcon name="gear" size={16} color={C.text}/>} label="IF/THEN Rule" large onClick={()=>setShowRules(true)}/>
            </RGroup>
            <RGroup label="Include">
              <RBtn icon={<>&#9901;</>} label="Attach File" large onClick={()=>addToast("Attachments supported in sequence builder","info")}/>
              <RBtn icon={<RIcon name="link" size={16} color={C.text}/>} label="Hyperlink" large onClick={()=>{const url=prompt("URL:","https://");if(url){const q=quillRef.current?.getEditor?.();if(q){const r=q.getSelection(true);q.formatText(r?.index||0,r?.length||0,"link",url);}}}}/>
              <RBtn icon={<RIcon name="calendar" size={16} color={C.text}/>} label="Date" large onClick={()=>{const q=quillRef.current?.getEditor?.();if(q){const idx=q.getLength();q.insertText(idx,new Date().toLocaleDateString());}}}/>
            </RGroup>
          </>
        )}

        {ribbonTab==="format"&&(
          <>
            <RGroup label="Font">
              <RBtn icon="B" label="Bold" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("bold",!q.getFormat().bold);}}/>
              <RBtn icon="I" label="Italic" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("italic",!q.getFormat().italic);}}/>
              <RBtn icon="U" label="Underline" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("underline",!q.getFormat().underline);}}/>
              <RBtn icon="S" label="Strike" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("strike",!q.getFormat().strike);}}/>
            </RGroup>
            <RGroup label="Paragraph">
              <RBtn icon={<>&#8226;</>} label="Bullets" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("list","bullet");}}/>
              <RBtn icon="#" label="Numbering" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("list","ordered");}}/>
              <RBtn icon="L" label="Left" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("align","left");}}/>
              <RBtn icon="C" label="Center" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("align","center");}}/>
              <RBtn icon="R" label="Right" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("align","right");}}/>
              <RBtn icon="|" label="Indent" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("indent","+1");}}/>
            </RGroup>
            <RGroup label="Styles">
              <RBtn icon="H1" label="Heading 1" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("header",1);}}/>
              <RBtn icon="H2" label="Heading 2" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("header",2);}}/>
              <RBtn icon={<>&#8213;</>} label="Normal" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("header",false);}}/>
              <RBtn icon={<>&#10077;</>} label="Quote" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q)q.format("blockquote",true);}}/>
              <RBtn icon={<>&#10005;</>} label="Clear Format" onClick={()=>{const q=quillRef.current?.getEditor?.();if(q){const r=q.getSelection();if(r)q.removeFormat(r.index,r.length);}}}/>
            </RGroup>
          </>
        )}

        {/* Layout tab removed — Margins/Orientation/Page Size/Columns/paragraph
            Spacing are print-document concepts with no equivalent in an HTML
            email composer; building fake depth for them had no real product
            value, so this tab (and its RIBBON_TABS entry) was dropped. */}

        {ribbonTab==="view"&&(
          <>
            {/* Print Layout/Reading View/Draft view/Ruler/Formatting Marks/
                Navigation Pane removed for the same reason as the Layout tab —
                there's only one real view of an email being composed here. */}
            <RGroup label="Zoom">
              <RBtn icon="+" label="Zoom In" onClick={()=>setZoomPct(p=>Math.min(200,p+10))}/>
              <RBtn icon="-" label="Zoom Out" onClick={()=>setZoomPct(p=>Math.max(50,p-10))}/>
              <RBtn icon={<>&#9678;</>} label={zoomPct+"%"} active={zoomPct===100} onClick={()=>setZoomPct(100)}/>
              <RBtn icon={<>&#8660;</>} label="Page Width" onClick={()=>setZoomPct(100)}/>
            </RGroup>
            <RGroup label="Stats">
              <div style={{padding:"4px 6px",display:"flex",flexDirection:"column",gap:3}}>
                {[["Words",wordCount.words,C.accent],["Chars",wordCount.chars,C.text],["Read Time",wordCount.readTime,C.green]].map(([l,v,c])=>(
                  <div key={l} style={{display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{fontSize:10,color:C.textSub,minWidth:55}}>{l}:</span>
                    <span style={{fontSize:11,fontWeight:600,color:c,fontFamily:"monospace"}}>{v}</span>
                  </div>
                ))}
              </div>
            </RGroup>
          </>
        )}

        {ribbonTab==="options"&&(
          <RGroup label="Word Options">
            <RBtn icon={<RIcon name="gear" size={17} color={C.text}/>} label="Word Options" large onClick={()=>setShowWordOptions(true)} active/>
            <RBtn icon={<RIcon name="search" size={17} color={C.text}/>} label="Find & Replace" large onClick={()=>setShowFindReplace(true)}/>
            <RBtn icon={<RIcon name="save" size={17} color={C.text}/>} label="Auto Save" large active={wordOptions.autoSave} onClick={()=>setWordOptions(p=>({...p,autoSave:!p.autoSave}))}/>
            <RBtn icon={<>&#8630;</>} label="Undo" large onClick={()=>quillRef.current?.getEditor?.().history.undo()}/>
            <RBtn icon={<>&#8631;</>} label="Redo" large onClick={()=>quillRef.current?.getEditor?.().history.redo()}/>
          </RGroup>
        )}

        {ribbonTab==="review"&&(
          <>
            <RGroup label="Proofing">
              <RBtn icon="ABC" label="Spell Check" large onClick={()=>runProofCheck("spell")}/>
              <RBtn icon={<>&#182;</>} label="Grammar" large onClick={()=>runProofCheck("grammar")}/>
            </RGroup>
            <RGroup label="Merge Check">
              <RBtn icon={<>&#10003;</>} label="Check Errors" large onClick={()=>{
                const missing=FOLLOW_UPS.filter(f=>!drafts[f.id].subject||!drafts[f.id].body);
                if(missing.length===0){addToast(" All templates complete  ready to merge!","success");}
                else{addToast(`Missing content: ${missing.map(f=>f.label).join(", ")}`,"error");}
              }}/>
              <RBtn icon={<>&#9993;</>} label="Validate Emails" large onClick={()=>{
                const invalid=filteredRecipients.filter(r=>!r.email?.includes("@"));
                addToast(invalid.length===0?` All ${filteredRecipients.length} emails valid`:`${invalid.length} invalid emails found`,"success");
                if(invalid.length===0&&filteredRecipients.length>0)setEmailsValidated(true);
              }}/>
              {/* Was a toast-only stub ("Detected columns: ...") — the
                  Mailings tab's own "Match Fields" button (further up this
                  file) already got the real column->merge-tag mapping grid
                  built for it; this one was just never updated to match and
                  kept the old placeholder behavior. Reuses the exact same
                  openMapFieldsFor grid against the currently-loaded
                  recipients (no new UI, no new logic) instead of duplicating
                  it a second way. */}
              <RBtn icon={<>&#8644;</>} label="Match Fields" large onClick={()=>{
                if(!recipients.length){addToast("Load a recipient list first","error");return;}
                openMapFieldsFor(recipients,"current recipients");
              }}/>
            </RGroup>
            <RGroup label="Stats">
              <div style={{padding:"4px 6px",display:"flex",flexDirection:"column",gap:3}}>
                {[["Sent",mergeStats.sent,C.accent],["Opens",mergeStats.opens,C.amber],["Replies",mergeStats.replies,C.green]].map(([l,v,c])=>(
                  <div key={l} style={{display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{fontSize:10,color:C.textSub,minWidth:40}}>{l}:</span>
                    <span style={{fontSize:12,fontWeight:600,color:c,fontFamily:"monospace"}}>{v}</span>
                  </div>
                ))}
              </div>
            </RGroup>
          </>
        )}
      </div>

      {/* Main content */}
      <div style={{display:"flex",flexDirection:isMobile?"column":"row",flex:1,overflow:isMobile?"auto":"hidden"}}>

        {/* Stage rail (redesign plan, Phase 3) — informational progress
            tracker, not a wizard: every existing control below is still
            reachable exactly as before, this just adds an at-a-glance
            "where am I in this send" view and one-click focus. */}
        {!isMobile&&<MailMergeStageRail stages={stageStatus} onStageClick={handleStageClick} collapsed={railCollapsed} onToggleCollapse={()=>setRailCollapsed(v=>!v)}/>}

        {/* Left panel  steps + settings */}
        <div style={{width:isMobile?"100%":220,background:"#fff",borderRight:isMobile?"none":"1px solid #d1cec9",borderBottom:isMobile?"1px solid #d1cec9":"none",display:"flex",flexDirection:"column",flexShrink:0,overflowY:"auto"}}>
          {/* From sender */}
          <div style={{padding:"10px 12px",borderBottom:"1px solid #edebe9"}}>
            <p style={{fontSize:10,fontWeight:700,color:C.textSub,textTransform:"uppercase",marginBottom:4}}>From Account</p>
            <select value={fromEmail} onChange={e=>setFromEmail(e.target.value)}
              style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,background:"#fff",outline:"none"}}>
              {senders.map(s=><option key={s.id} value={s.email}>{s.name} ({s.email})</option>)}
              {!senders.length&&<option value={settings.senderEmail||""}>{settings.sdrName} ({settings.senderEmail})</option>}
            </select>
          </div>
          {/* Recipients */}
          <div style={{padding:"10px 12px",borderBottom:"1px solid #edebe9"}}>
            <p style={{fontSize:10,fontWeight:700,color:C.textSub,textTransform:"uppercase",marginBottom:6}}>Recipients</p>
            <select ref={fileSelectRef} value={selectedFile} onChange={e=>{const v=e.target.value;setSelectedFile("");setTimeout(()=>loadFile(v),0);}}
              style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,background:"#fff",outline:"none",marginBottom:6}}>
              <option value="">Select contact file...</option>
              {contactFiles.map(f=><option key={f.id} value={f.name}>{f.name} ({f.count})</option>)}
            </select>
            <button onClick={importCSV}
              style={{width:"100%",background:"#f3f2f1",border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:11,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",color:C.text,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
              Import CSV / Excel
            </button>
            <button onClick={()=>setShowManualEntry(!showManualEntry)}
              style={{width:"100%",background:showManualEntry?C.accentL:"#f3f2f1",border:"1px solid "+(showManualEntry?C.accent:"#d1cec9"),borderRadius:3,padding:"5px 8px",fontSize:11,cursor:"pointer",fontFamily:"Segoe UI,sans-serif",color:showManualEntry?C.accent:C.text,display:"flex",alignItems:"center",justifyContent:"center",gap:4,marginTop:6}}>
              Add Emails Manually
            </button>
            {showManualEntry&&(
              <div style={{marginTop:6}}>
                <textarea value={manualEmails} onChange={e=>setManualEmails(e.target.value)}
                  placeholder="Paste or type emails, separated by commas or new lines"
                  rows={4}
                  style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 8px",fontSize:11,color:C.text,outline:"none",resize:"vertical",fontFamily:"'Segoe UI',sans-serif",boxSizing:"border-box"}}/>
                <button onClick={addManualRecipients}
                  style={{width:"100%",background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"5px 8px",fontSize:11,cursor:"pointer",fontWeight:600,marginTop:4}}>
                  + Add to Recipients
                </button>
              </div>
            )}
            {recipients.length>0&&(
              <div style={{marginTop:6,padding:"5px 8px",background:"#dff6dd",border:"1px solid #107c1044",borderRadius:3}}>
                <p style={{fontSize:11,color:C.green,fontWeight:600}}>{recipients.length} recipients loaded</p>
              </div>
            )}
          </div>
          <div style={{padding:"8px 8px",flex:1}}>
            <p style={{fontSize:10,fontWeight:700,color:C.textSub,textTransform:"uppercase",marginBottom:6,padding:"0 4px"}}>Email Templates</p>
            <select value={activeStep} onChange={e=>{const id=e.target.value;if(!openTabs.includes(id))setOpenTabs(p=>[...p,id]);setActiveStep(id);setMinimized(p=>({...p,[id]:false}));}} style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 8px",fontSize:12,color:C.text,background:"#fff",outline:"none",cursor:"pointer"}}>
              {FOLLOW_UPS.map((fu,i)=>(<option key={fu.id} value={fu.id}>{fu.label}</option>))}
            </select>
          </div>

          {/* Summary */}
          <div style={{padding:"10px 12px",borderTop:"1px solid #d1cec9",background:"#f8f7f6"}}>
            <p style={{fontSize:10,fontWeight:700,color:C.textSub,textTransform:"uppercase",marginBottom:4}}>Merge Summary</p>
            {[
              ["Recipients",filteredRecipients.length,C.accent],
              ["Templates",FOLLOW_UPS.filter(f=>drafts[f.id].body).length+"/"+FOLLOW_UPS.length,C.green],
              ["Total Emails",filteredRecipients.length*FOLLOW_UPS.filter(f=>drafts[f.id].body).length,C.purple],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <span style={{fontSize:11,color:C.textSub}}>{l}</span>
                <span style={{fontSize:11,fontWeight:600,color:c,fontFamily:"monospace"}}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center  tabbed editor */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"#e8e6e4"}}>
          {/* Tab bar - now in title bar above */}
          <div style={{display:"none"}}>
            {openTabs.map(tabId=>{
              const fu=FOLLOW_UPS.find(f=>f.id===tabId);
              if(!fu)return null;
              const isActive=activeStep===tabId;
              const isMin=minimized[tabId];
              return(
                <div key={tabId}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",background:isActive?"#fff":"#e8e6e4",border:"1px solid #d1cec9",borderBottom:isActive?"1px solid #fff":"1px solid #d1cec9",borderRadius:"4px 4px 0 0",cursor:"pointer",marginRight:3,minWidth:140,flexShrink:0,marginBottom:-1}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:fu.color,flexShrink:0}}/>
                  <span onClick={()=>{setActiveStep(tabId);setMinimized(p=>({...p,[tabId]:false}));}}
                    style={{fontSize:11,fontWeight:isActive?600:400,color:isActive?C.text:C.textSub,flex:1,whiteSpace:"nowrap"}}>
                    {fu.label}
                  </span>
                  <div style={{display:"flex",gap:2,flexShrink:0}}>
                    <button onClick={e=>{e.stopPropagation();setMinimized(p=>({...p,[tabId]:!p[tabId]}));setActiveStep(tabId);}}
                      title={isMin?"Restore":"Minimize"}
                      style={{width:14,height:14,borderRadius:2,border:"1px solid #d1cec9",background:"#fff4ce",cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"#d83b01"}}>
                      {isMin?"":""}
                    </button>
                    <button onClick={e=>{e.stopPropagation();setMaximized(p=>({...p,[tabId]:!p[tabId]}));setActiveStep(tabId);}}
                      title="Maximize"
                      style={{width:14,height:14,borderRadius:2,border:"1px solid #d1cec9",background:"#dff6dd",cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"#107c10"}}>
                      {maximized[tabId]?"":""}
                    </button>
                    <button onClick={e=>{e.stopPropagation();const newTabs=openTabs.filter(t=>t!==tabId);setOpenTabs(newTabs);if(activeStep===tabId)setActiveStep(newTabs[newTabs.length-1]||"initial");}}
                      title="Close"
                      style={{width:14,height:14,borderRadius:2,border:"1px solid #d1cec9",background:"#fde7e9",cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"#a4262c"}}>
                      
                    </button>
                  </div>
                </div>
              );
            })}
            {openTabs.length<FOLLOW_UPS.length&&(
              <button onClick={()=>{const next=FOLLOW_UPS.find(f=>!openTabs.includes(f.id));if(next){setOpenTabs(p=>[...p,next.id]);setActiveStep(next.id);}}}
                style={{padding:"4px 10px",background:"transparent",border:"none",color:C.accent,fontSize:18,cursor:"pointer",marginBottom:4}}>+</button>
            )}
          </div>

          {/* Active tab content */}
          {openTabs.map(tabId=>{
            const fu=FOLLOW_UPS.find(f=>f.id===tabId);
            if(!fu||activeStep!==tabId||minimized[tabId])return null;
            const isMax=maximized[tabId];
            const tabPreview=previewMode&&filteredRecipients[previewIdx];
            const tabPreviewSubject=tabPreview?applyMerge(drafts[tabId].subject,filteredRecipients[previewIdx]):"";
            const tabPreviewBody=tabPreview?applyMerge(applyRule(drafts[tabId].body,filteredRecipients[previewIdx]),filteredRecipients[previewIdx]):"";
            return(
              <div key={tabId} style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",position:isMax?"fixed":"relative",inset:isMax?"0":"auto",zIndex:isMax?9990:1,background:"#e8e6e4"}}>
                {/* Editor header */}
                <div style={{background:"#fff",borderBottom:"1px solid #d1cec9",padding:"6px 16px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:fu.color}}/>
                  <span style={{fontSize:13,fontWeight:600,color:C.text}}>{fu.label}</span>
                  <span style={{fontSize:11,color:C.textSub,background:fu.color+"22",padding:"1px 8px",borderRadius:3}}>{fu.day}</span>
                  <div style={{flex:1}}/>
                  {isMax&&(
                    <button onClick={()=>setMaximized(p=>({...p,[tabId]:false}))}
                      style={{background:"#f3f2f1",border:"1px solid #d1cec9",borderRadius:3,padding:"3px 10px",fontSize:11,cursor:"pointer",marginRight:8}}>
                       Restore
                    </button>
                  )}
                  <button onClick={()=>{setActiveStep(tabId);aiGenerateStep();}} disabled={aiLoading}
                    style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"4px 12px",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                    {aiLoading?<><span style={{animation:"spin .7s linear infinite",display:"inline-block"}}></span> Generating...</>:" AI Draft"}
                  </button>
                  {/* Voice dictation: speak the email, an AI agent refines it into
                      the draft live. Scoped to whichever tab is active — clicking
                      it on a non-active tab switches to that tab first so the
                      dictated content lands where the button was clicked. */}
                  <button onClick={()=>{setActiveStep(tabId);voice.toggleListening();}} disabled={!voice.supported}
                    title={!voice.supported?"Voice dictation needs Chrome or Edge":(voice.listening&&tabId===activeStep?"Stop voice dictation":"Dictate this email by voice")}
                    style={{background:voice.listening&&tabId===activeStep?"#d13438":"#fff",color:voice.listening&&tabId===activeStep?"#fff":C.text,border:"1px solid "+(voice.listening&&tabId===activeStep?"#d13438":"#d1cec9"),borderRadius:3,padding:"4px 12px",fontSize:11,cursor:voice.supported?"pointer":"not-allowed",display:"flex",alignItems:"center",gap:4,opacity:voice.supported?1:0.5,marginLeft:4}}>
                    {voice.listening&&tabId===activeStep?<><span style={{width:7,height:7,borderRadius:"50%",background:"#fff",display:"inline-block",animation:"spin 1s linear infinite"}}/> {voice.busy?"Updating…":"Listening"}</>:<>&#127908; Voice</>}
                  </button>
                </div>
                {tabId===activeStep&&voice.listening&&voice.interimText&&(
                  <div style={{background:"#fff9e6",borderBottom:"1px solid #f0e2a0",padding:"4px 16px",fontSize:11,color:C.textSub,fontStyle:"italic",flexShrink:0}}>
                    {voice.interimText}
                  </div>
                )}
                {/* Subject */}
                <div style={{background:"#fff",borderBottom:"1px solid #e8e8e8",padding:"0",display:"flex",alignItems:"center",minHeight:32,flexShrink:0}}>
                  <button style={{minWidth:80,padding:"5px 10px",fontSize:12,fontWeight:400,color:"#333",border:"1px solid #c0c0c0",borderRadius:3,flexShrink:0,background:"#fff",cursor:"default",textAlign:"center",fontFamily:"Segoe UI,sans-serif",outline:"none",margin:"3px 8px"}}>Subject</button>
                  <input ref={tabId===activeStep?subjectRef:undefined}
                    value={tabPreview?tabPreviewSubject:drafts[tabId].subject}
                    onChange={e=>!previewMode&&setDrafts(p=>({...p,[tabId]:{...p[tabId],subject:e.target.value}}))}
                    onFocus={()=>{setSubjectFocus(true);lastFocRef.current="subject";}}
                    onBlur={()=>setSubjectFocus(false)}
                    placeholder=""
                    style={{flex:1,border:"none",outline:"none",fontSize:13,color:C.text,background:"transparent",fontFamily:"Segoe UI,Calibri,sans-serif",padding:"6px 12px"}}
                    readOnly={!!previewMode}/>
                  {tabPreview&&(
                    <MergePreviewBar recipient={filteredRecipients[previewIdx]} index={previewIdx} total={filteredRecipients.length} navigable theme={C} compact
                      onFirst={()=>setPreviewIdx(0)} onPrev={()=>setPreviewIdx(p=>Math.max(0,p-1))}
                      onNext={()=>setPreviewIdx(p=>Math.min(filteredRecipients.length-1,p+1))} onLast={()=>setPreviewIdx(filteredRecipients.length-1)}/>
                  )}
                </div>
                {/* Editor */}
                <div style={{flex:1,overflow:"auto",padding:"20px",display:"flex",justifyContent:"center"}}>
                  <div style={{width:"100%",maxWidth:isMax?"100%":780,background:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,.12)",minHeight:500,display:"flex",flexDirection:"column"}}>
                    {tabPreview?(
                      <div style={{flex:1,padding:"40px 60px",fontSize:14,color:C.text,lineHeight:1.8,fontFamily:"Calibri,sans-serif"}}
                        dangerouslySetInnerHTML={{__html:tabPreviewBody||"<p style='color:#a19f9d'>No content yet.</p>"}}/>
                    ):(
                      <div className="mm-quill" style={{flex:1,display:"flex",flexDirection:"column",fontSize:zoomPct+"%"}}>
                        <RichTextEditor ref={tabId===activeStep?quillRef:undefined}
                          value={drafts[tabId].body||"<p><br></p>"}
                          onChange={val=>setDrafts(p=>({...p,[tabId]:{...p[tabId],body:val}}))}
                          formatMarksOn={formatMarksOn}
                          placeholder=""
                          onFocus={()=>{lastFocRef.current="body";setActiveStep(tabId);registerCleanPaste(quillRef.current?.getEditor?.());}}
                          style={{flex:1}}/>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Empty state when all minimized */}
          {openTabs.length>0&&openTabs.every(t=>minimized[t]||activeStep!==t)&&(
            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8,color:C.textSub}}>
              <p style={{fontSize:32}}></p>
              <p style={{fontSize:14}}>Click a tab above to restore it</p>
            </div>
          )}
        </div>

        {/* Right panel  preview / recipient list */}
        <div style={{width:isMobile?"100%":260,background:"#fff",borderLeft:isMobile?"none":"1px solid #d1cec9",borderTop:isMobile?"1px solid #d1cec9":"none",display:"flex",flexDirection:"column",flexShrink:0,overflowY:"auto"}}>
          <div style={{padding:"10px 12px",borderBottom:"1px solid #edebe9",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <p style={{fontSize:12,fontWeight:600,color:C.text}}>Recipients ({filteredRecipients.length})</p>
            {/* Was setPreviewMode(!previewMode) — see the ribbon's own
                "Preview Results" button above for why this now opens the
                full-window workspace instead. */}
            <button onClick={()=>setShowFullPreview(true)}
              style={{background:"#f3f2f1",color:C.textSub,border:"1px solid "+C.border,borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer"}}>
               Preview
            </button>
          </div>
          <div style={{padding:"6px 12px",borderBottom:"1px solid #edebe9"}}>
            <input ref={recipientSearchRef} value={searchQ} onChange={e=>setSearchQ(e.target.value)}
              placeholder=" Search recipients..."
              style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{flex:1,overflowY:"auto"}}>
            {filteredRecipients.length===0&&(
              <div style={{padding:"24px 12px",textAlign:"center",color:C.textSub}}>
                <p style={{fontSize:24,marginBottom:6}}></p>
                <p style={{fontSize:12}}>No recipients yet.</p>
                <p style={{fontSize:11,color:C.textMuted,marginTop:4}}>Import a file or select a contact list.</p>
              </div>
            )}
            {filteredRecipients.map((r,i)=>(
              <div key={r.id||i} onClick={()=>{setPreviewIdx(i);setShowFullPreview(true);}}
                style={{padding:"7px 12px",borderBottom:"1px solid #edebe9",cursor:"pointer",background:previewMode&&previewIdx===i?"#deecf9":"transparent",borderLeft:previewMode&&previewIdx===i?"3px solid "+C.accent:"3px solid transparent"}}>
                <p style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:1}}>{r.name||r.email}</p>
                <p style={{fontSize:11,color:C.textSub,marginBottom:1}}>{r.company}</p>
                <p style={{fontSize:10,color:C.textMuted}}>{r.email}</p>
              </div>
            ))}
          </div>
          {/* Finish & Merge actions */}
          <div style={{padding:"10px 12px",borderTop:"1px solid #d1cec9",background:"#f8f7f6",display:"flex",flexDirection:"column",gap:6}}>
            <p style={{fontSize:10,fontWeight:700,color:C.textSub,textTransform:"uppercase",marginBottom:2}}>Finish & Merge</p>
            <button onClick={finishSend} disabled={sending||filteredRecipients.length===0}
              style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"7px 12px",fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:4,opacity:filteredRecipients.length===0?.5:1}}>
              {sending?<><span style={{animation:"spin .7s linear infinite",display:"inline-block"}}></span> Sending...</>:` Send to ${filteredRecipients.length} recipients`}
            </button>
            <button onClick={assignToSequence}
              style={{background:"#fff",color:C.text,border:"1px solid #d1cec9",borderRadius:3,padding:"6px 12px",fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"}}>
               Assign to Sequence
            </button>
            <button onClick={createCampaignFromMerge} disabled={creatingCampaign||filteredRecipients.length===0}
              style={{background:"#fff",color:C.text,border:"1px solid #d1cec9",borderRadius:3,padding:"6px 12px",fontSize:12,cursor:creatingCampaign||filteredRecipients.length===0?"not-allowed":"pointer",fontFamily:"'Segoe UI',sans-serif",opacity:filteredRecipients.length===0?.5:1}}>
               {creatingCampaign?"Creating…":"Create Campaign"}
            </button>
            <button onClick={()=>saveTemplate()}
              style={{background:"#fff",color:C.text,border:"1px solid #d1cec9",borderRadius:3,padding:"6px 12px",fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"}}>
               Save as Template
            </button>
          </div>
        </div>
      </div>

      <MailMergeFullPreview
        open={showFullPreview}
        onClose={()=>setShowFullPreview(false)}
        recipients={filteredRecipients}
        initialIndex={previewIdx}
        steps={FOLLOW_UPS}
        drafts={drafts}
        setDrafts={setDrafts}
        initialStepId={activeStep}
        applyMerge={applyMerge}
        applyRule={applyRule}
        zoomPct={zoomPct}
        setZoomPct={setZoomPct}
        docFilename="email-message"
        MERGE_TAGS={MERGE_TAGS}
        MERGE_TAG_LABELS={MERGE_TAG_LABELS}
        otherMergeFields={otherMergeFields}
        onMatchFields={()=>{
          if(!recipients.length){addToast("Load a recipient list first","error");return;}
          openMapFieldsFor(recipients,"current recipients");
        }}
        checkProofIssues={checkProofIssues}
        finishSend={finishSend}
        scheduleMergeSend={scheduleMergeSend}
        assignToSequence={assignToSequence}
        saveDraftReal={saveDraftReal}
        sending={sending}
        savingDraft={savingDraft}
        scheduling={scheduling}
        scheduleAt={scheduleAt}
        setScheduleAt={setScheduleAt}
        setEmailsValidated={setEmailsValidated}
        addToast={addToast}
      />

      {/* Subject Rotation dialog */}
      {showRotation&&(
        <Modal open={showRotation} onClose={()=>setShowRotation(false)} width={640} ariaLabel="Rotation & Testing">
          <div style={{maxHeight:"85vh",overflowY:"auto",fontFamily:"'Segoe UI',sans-serif"}}>
            <div style={{background:C.nav,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0}}>
              <p style={{fontSize:13,fontWeight:600,color:"#fff"}}>⟳ Rotation & Testing — Rotation-Enabled Sending</p>
              <button onClick={()=>setShowRotation(false)} style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:20}}>
              {creatingRun?(
                <p style={{fontSize:12,color:C.textSub}}>Setting up a rotation-enabled run from your current recipients and initial email…</p>
              ):!mmRun?(
                <p style={{fontSize:12,color:C.textSub}}>Load recipients and write the initial email, then reopen this panel to set up rotation.</p>
              ):(
                <>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.bg,borderRadius:6,marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.textSub}}>
                      <StatusChip sm bucket={{Draft:"draft",Sending:"sending",Paused:"paused",Completed:"completed",Canceled:"stopped"}[mmRun.status]||"stopped"} label={mmRun.status}/>
                      {mmRun.sent||0} sent{mmRun.failed?`, ${mmRun.failed} failed`:""} of {mmRun.totalRecipients||0}
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      {(mmRun.status==="Draft"||mmRun.status==="Paused")&&(
                        <button onClick={()=>runAction("start")} disabled={runActionLoading} style={{background:C.green,color:"#fff",border:"none",borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:runActionLoading?"not-allowed":"pointer"}}>▶ {mmRun.status==="Paused"?"Resume":"Start Sending"}</button>
                      )}
                      {mmRun.status==="Sending"&&(
                        <button onClick={()=>runAction("pause")} disabled={runActionLoading} style={{background:C.amber,color:"#fff",border:"none",borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:runActionLoading?"not-allowed":"pointer"}}>⏸ Pause</button>
                      )}
                      {["Draft","Paused","Sending"].includes(mmRun.status)&&(
                        <button onClick={()=>{if(window.confirm("Cancel this rotation-enabled run? This cannot be undone."))runAction("cancel");}} disabled={runActionLoading} style={{background:"#fff",color:C.red,border:"1px solid "+C.red,borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:runActionLoading?"not-allowed":"pointer"}}>Cancel</button>
                      )}
                      {mmRun.status==="Draft"&&(
                        <button onClick={saveRunEdits} disabled={savingRunEdits} title="Push the current Initial Email tab's subject/body to this run"
                          style={{background:"#fff",color:C.accent,border:"1px solid "+C.accent,borderRadius:3,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:savingRunEdits?"not-allowed":"pointer"}}>
                          {savingRunEdits?"Saving…":"Save Changes"}
                        </button>
                      )}
                      <button onClick={openMmVersionHistory} title="Version history" style={{background:"#fff",color:C.textSub,border:"1px solid #d1cec9",borderRadius:3,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>History</button>
                      <button onClick={()=>refreshMmRun(mmRun.id)} style={{background:"#fff",color:C.textSub,border:"1px solid #d1cec9",borderRadius:3,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>⟳</button>
                    </div>
                  </div>
                  <RotationTestingPanel ownerType="mailmerge" ownerId={mmRun.id} theme={C}/>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Mail Merge run — version history */}
      {showMmVersions&&(
        <Modal open={showMmVersions} onClose={()=>setShowMmVersions(false)} width={560} ariaLabel="Version History">
          <div style={{maxHeight:"80vh",display:"flex",flexDirection:"column",fontFamily:"'Segoe UI',sans-serif"}}>
            <div style={{background:C.nav,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <p style={{fontSize:13,fontWeight:600,color:"#fff"}}>Version History</p>
              <button onClick={()=>setShowMmVersions(false)} style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:16,overflowY:"auto",flex:1}}>
              <p style={{fontSize:12,color:C.textSub,marginBottom:12,lineHeight:1.4}}>Every "Save Changes" keeps the version it replaced. Revert to bring an older version back — reverting itself saves the current content as a version first, so nothing is lost.</p>
              <div style={{border:"1px solid #d1cec9",borderRadius:4}}>
                {mmVersionsLoading?(
                  <div style={{padding:20,textAlign:"center",color:C.textSub,fontSize:12}}>Loading…</div>
                ):mmVersionsList.length===0?(
                  <div style={{padding:20,textAlign:"center",color:C.textSub,fontSize:12}}>No earlier versions yet.</div>
                ):mmVersionsList.map((v,i)=>(
                  <div key={v.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderBottom:i<mmVersionsList.length-1?"1px solid #eee":"none"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{new Date(v.createdAt).toLocaleString()}</div>
                      <div style={{fontSize:11,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.reason==="revert"?"Before a revert":"Before a save"} · {v.subject||"(no subject)"}</div>
                    </div>
                    <button onClick={()=>revertMmVersion(v)} disabled={mmRevertingId===v.id||mmRun?.status!=="Draft"}
                      title={mmRun?.status!=="Draft"?"Only a Draft run can be reverted":""}
                      style={{background:"#f4f0ff",color:C.purple,border:"none",borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:(mmRevertingId===v.id||mmRun?.status!=="Draft")?"not-allowed":"pointer",flexShrink:0}}>
                      {mmRevertingId===v.id?"Reverting…":"Revert"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Rules dialog */}
      {showRules&&(
        <Modal open={showRules} onClose={()=>setShowRules(false)} width={500} ariaLabel="Insert Conditional Rule">
          <div style={{fontFamily:"'Segoe UI',sans-serif"}}>
            <div style={{background:C.nav,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <p style={{fontSize:13,fontWeight:600,color:"#fff"}}> Insert Conditional Rule (IF/THEN/ELSE)</p>
              <button onClick={()=>setShowRules(false)} style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}></button>
            </div>
            <div style={{padding:20}}>
              <p style={{fontSize:12,color:C.textSub,marginBottom:14}}>Create conditional content based on recipient data fields.</p>
              <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                {[["Field","field",["Company","Title","Industry","Location","Email"]],["Operator","operator",["equals","contains","starts with","ends with"]],["Value","value",null]].map(([l,k,opts])=>(
                  <div key={k}>
                    <p style={{fontSize:11,fontWeight:600,color:C.text,marginBottom:4}}>{l}</p>
                    {opts?(
                      <select value={rule[k]} onChange={e=>setRule(p=>({...p,[k]:e.target.value}))}
                        style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 8px",fontSize:12,color:C.text,outline:"none"}}>
                        {opts.map(o=><option key={o}>{o}</option>)}
                      </select>
                    ):(
                      <input value={rule[k]} onChange={e=>setRule(p=>({...p,[k]:e.target.value}))}
                        placeholder="e.g. Healthcare"
                        style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 8px",fontSize:12,color:C.text,outline:"none"}}/>
                    )}
                  </div>
                ))}
              </div>
              <div style={{marginBottom:10}}>
                <p style={{fontSize:11,fontWeight:600,color:C.green,marginBottom:4}}>IF MATCH  Show this content:</p>
                <textarea value={rule.ifContent} onChange={e=>setRule(p=>({...p,ifContent:e.target.value}))}
                  placeholder="Content shown when condition matches..."
                  rows={3} style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 8px",fontSize:12,resize:"vertical",fontFamily:"'Segoe UI',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{marginBottom:14}}>
                <p style={{fontSize:11,fontWeight:600,color:C.amber,marginBottom:4}}>ELSE  Show this content:</p>
                <textarea value={rule.elseContent} onChange={e=>setRule(p=>({...p,elseContent:e.target.value}))}
                  placeholder="Content shown when condition does not match..."
                  rows={3} style={{width:"100%",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 8px",fontSize:12,resize:"vertical",fontFamily:"'Segoe UI',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{background:"#f8f7f6",border:"1px solid #d1cec9",borderRadius:3,padding:"8px 12px",marginBottom:14,fontSize:11,color:C.textSub,fontFamily:"monospace"}}>
                [[IF:{rule.field||"Company"}={rule.value||"value"}]]<br/>
                {rule.ifContent||"[IF content]"}<br/>
                [[ELSE]]<br/>
                {rule.elseContent||"[ELSE content]"}<br/>
                [[ENDIF]]
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>setShowRules(false)} style={{background:"#fff",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 16px",fontSize:12,cursor:"pointer"}}>Cancel</button>
                <button onClick={insertRuleTemplate} style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"6px 16px",fontSize:12,cursor:"pointer",fontWeight:600}}>Insert Rule</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Find & Replace Dialog */}
      <FindReplaceDialog open={showFindReplace} onClose={()=>setShowFindReplace(false)} getQuill={getQuill} addToast={addToast} navTheme={C.nav}/>

      {/* Paste Options popup */}
      {showPasteOptions&&(
        <ContextMenu open={showPasteOptions} onClose={()=>setShowPasteOptions(false)} x={pastePos.x} y={pastePos.y} width={0}>
          <div style={{padding:"6px 8px",fontFamily:"'Segoe UI',sans-serif"}}>
            <div style={{display:"flex",alignItems:"center",gap:2,marginBottom:5}}>
              <span style={{fontSize:10,color:"#605e5c",fontWeight:600,marginRight:3}}>Paste Options:</span>
              {[{icon:"",label:"Keep Source",mode:"html"},{icon:"",label:"Merge",mode:"merge"},{icon:"",label:"Text Only",mode:"text"}].map(opt=>(
                <button key={opt.mode} onClick={()=>pasteAs(opt.mode)} title={opt.label}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1,padding:"3px 6px",border:"1px solid #d1cec9",borderRadius:2,cursor:"pointer",background:"#f8f7f6",minWidth:44}}
                  onMouseEnter={e=>e.currentTarget.style.background="#deecf9"} onMouseLeave={e=>e.currentTarget.style.background="#f8f7f6"}>
                  <span style={{fontSize:14}}>{opt.icon}</span>
                  <span style={{fontSize:8,color:"#605e5c",whiteSpace:"nowrap"}}>{opt.label}</span>
                </button>
              ))}
            </div>
            <button onClick={()=>{setShowPasteOptions(false);setShowWordOptions(true);}} style={{background:"none",border:"none",color:"#0078d4",fontSize:10,cursor:"pointer",padding:0,fontFamily:"'Segoe UI',sans-serif",textDecoration:"underline"}}>Set Default Paste...</button>
          </div>
        </ContextMenu>
      )}
      
            
            
              
            

      {/* Word Options Dialog */}
      {showWordOptions&&(
        <Modal open={showWordOptions} onClose={()=>setShowWordOptions(false)} width={720} ariaLabel="Word Options">
          <div style={{height:560,fontFamily:"'Segoe UI',sans-serif",display:"flex",flexDirection:"column"}}>
            <div style={{background:C.nav,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <p style={{fontSize:13,fontWeight:600,color:"#fff"}}> Word Options</p>
              <button onClick={()=>setShowWordOptions(false)} style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}></button>
            </div>
            <div style={{display:"flex",flex:1,overflow:"hidden"}}>
              {/* Left nav */}
              <div style={{width:180,borderRight:"1px solid #d1cec9",background:"#f8f7f6",padding:"8px 0"}}>
                {["General","Display","Proofing","Save","Language","Accessibility","Advanced","Customize Ribbon","Quick Access Toolbar","Add-ins","Trust Center"].map((item,i)=>(
                  <div key={item} style={{padding:"8px 16px",cursor:"pointer",fontSize:12,color:i===6?C.accent:C.text,background:i===6?"#deecf9":"transparent",fontWeight:i===6?600:400,borderLeft:i===6?"3px solid "+C.accent:"3px solid transparent"}}
                    onClick={()=>addToast("Section: "+item,"info")}>
                    {item}
                  </div>
                ))}
              </div>
              {/* Right content */}
              <div style={{flex:1,overflowY:"auto",padding:20}}>
                <p style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:4}}>Advanced options for working with Mail Merge.</p>
                <p style={{fontSize:11,color:C.textSub,marginBottom:20}}>These settings apply to the Mail Merge editor.</p>

                <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:12,paddingBottom:4,borderBottom:"1px solid #d1cec9"}}>Editing options</p>
                {[
                  ["autoSave","Auto Save drafts every 30 seconds"],
                  ["spellCheck","Check spelling as you type"],
                  ["grammar","Check grammar as you type"],
                  ["smartSelect","When selecting, automatically select entire word"],
                  ["dragDrop","Allow text to be dragged and dropped"],
                  ["ctrlClick","Use CTRL + Click to follow hyperlink"],
                  ["autoComplete","Show AutoComplete suggestions"],
                  ["trackFormatting","Keep track of formatting"],
                ].map(([key,label])=>(
                  <label key={key} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:C.text,marginBottom:8}}>
                    <input type="checkbox" checked={wordOptions[key]} onChange={e=>setWordOptions(p=>({...p,[key]:e.target.checked}))} style={{width:14,height:14}}/>
                    {label}
                  </label>
                ))}

                <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:12,paddingBottom:4,borderBottom:"1px solid #d1cec9",marginTop:20}}>Cut, copy, and paste</p>
                <div style={{marginBottom:12}}>
                  <p style={{fontSize:12,color:C.text,marginBottom:6}}>Default paste behavior:</p>
                  <select value={wordOptions.pasteDefault} onChange={e=>setWordOptions(p=>({...p,pasteDefault:e.target.value}))}
                    style={{border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,outline:"none",width:280}}>
                    <option value="keepSource">Keep Source Formatting</option>
                    <option value="mergeFormatting">Merge Formatting</option>
                    <option value="text">Keep Text Only</option>
                    <option value="destStyles">Use Destination Styles</option>
                  </select>
                </div>

                <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:12,paddingBottom:4,borderBottom:"1px solid #d1cec9",marginTop:20}}>Display</p>
                {[
                  ["showRuler","Show ruler"],
                  ["showFormattingMarks","Show formatting marks ()"],
                ].map(([key,label])=>(
                  <label key={key} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:C.text,marginBottom:8}}>
                    <input type="checkbox" checked={wordOptions[key]} onChange={e=>setWordOptions(p=>({...p,[key]:e.target.checked}))} style={{width:14,height:14}}/>
                    {label}
                  </label>
                ))}

                <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:12,paddingBottom:4,borderBottom:"1px solid #d1cec9",marginTop:20}}>Default font</p>
                <div style={{display:"flex",gap:12}}>
                  <div>
                    <p style={{fontSize:11,color:C.textSub,marginBottom:4}}>Font:</p>
                    <select value={wordOptions.defaultFont} onChange={e=>setWordOptions(p=>({...p,defaultFont:e.target.value}))}
                      style={{border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,outline:"none",width:180}}>
                      {["Calibri","Arial","Times New Roman","Georgia","Verdana","Courier New"].map(f=><option key={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <p style={{fontSize:11,color:C.textSub,marginBottom:4}}>Size:</p>
                    <select value={wordOptions.defaultSize} onChange={e=>setWordOptions(p=>({...p,defaultSize:e.target.value}))}
                      style={{border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,outline:"none",width:80}}>
                      {["10","11","12","14","16","18"].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div style={{padding:"10px 16px",borderTop:"1px solid #d1cec9",display:"flex",justifyContent:"flex-end",gap:8,background:"#f8f7f6"}}>
              <button onClick={()=>setShowWordOptions(false)} style={{background:"#fff",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 16px",fontSize:12,cursor:"pointer"}}>Cancel</button>
              <button onClick={()=>{setShowWordOptions(false);addToast("Word Options saved","success");}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"6px 16px",fontSize:12,cursor:"pointer",fontWeight:600}}>OK</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Template library */}
      {showTemplateLib&&(
        <Modal open={showTemplateLib} onClose={()=>setShowTemplateLib(false)} width={600} ariaLabel="Template Library">
          <div style={{maxHeight:"80vh",display:"flex",flexDirection:"column",fontFamily:"'Segoe UI',sans-serif"}}>
            <div style={{background:C.nav,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <p style={{fontSize:13,fontWeight:600,color:"#fff"}}> Template Library <span style={{fontWeight:400,opacity:.8,fontSize:11}}>(shared with Sequences &amp; Workspace)</span></p>
              <button onClick={()=>setShowTemplateLib(false)} aria-label="Close" style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}></button>
            </div>
            <div style={{display:"flex",gap:6,padding:"10px 16px 0"}}>
              {[{id:"all",label:"All"},...TEMPLATE_CATEGORIES].map(c=>(
                <button key={c.id} onClick={()=>setTemplateLibFilter(c.id)}
                  style={{padding:"4px 11px",borderRadius:99,border:"1px solid "+(templateLibFilter===c.id?C.accent:"#d1cec9"),background:templateLibFilter===c.id?"#deecf9":"#fff",color:templateLibFilter===c.id?C.accent:C.textSub,fontSize:11,fontWeight:600,cursor:"pointer"}}>{c.label}</button>
              ))}
            </div>
            <div style={{flex:1,overflowY:"auto",padding:16}}>
              {templates.filter(t=>templateLibFilter==="all"||(t.category||"general")===templateLibFilter).map(t=>(
                <div key={t.id} style={{border:"1px solid #d1cec9",borderRadius:4,padding:"12px 16px",marginBottom:10,cursor:"pointer"}}
                  onContextMenu={e=>{e.preventDefault();setTemplateCtxMenu({x:e.clientX,y:e.clientY,template:t});}}
                  onMouseEnter={e=>e.currentTarget.style.background="#f3f2f1"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4,gap:8}}>
                    {renamingTemplateId===t.id?(
                      <input autoFocus value={renameDraft} onChange={e=>setRenameDraft(e.target.value)}
                        onBlur={commitRenameTemplate} onKeyDown={e=>{if(e.key==="Enter")commitRenameTemplate();if(e.key==="Escape")setRenamingTemplateId(null);}}
                        style={{fontSize:13,fontWeight:600,color:C.text,border:"1px solid "+C.accent,borderRadius:3,padding:"2px 6px",flex:1,marginRight:8}}/>
                    ):(
                      <p style={{fontSize:13,fontWeight:600,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</p>
                    )}
                    <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:99,background:"#eef1f8",color:C.textSub,whiteSpace:"nowrap"}}>{TEMPLATE_CATEGORIES.find(c=>c.id===(t.category||"general"))?.label||"General"}</span>
                    <button onClick={()=>loadTemplate(t)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"4px 12px",fontSize:11,cursor:"pointer"}}>Load</button>
                  </div>
                  <p style={{fontSize:12,color:C.textSub,marginBottom:4}}>Subject: {t.subject}</p>
                  <div style={{fontSize:11,color:C.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} dangerouslySetInnerHTML={{__html:t.body}}/>
                </div>
              ))}
              {templates.filter(t=>templateLibFilter==="all"||(t.category||"general")===templateLibFilter).length===0&&(
                <p style={{fontSize:12,color:C.textMuted,textAlign:"center",padding:"20px 0"}}>No templates in this category yet.</p>
              )}
            </div>
            <div style={{padding:"10px 16px",borderTop:"1px solid #d1cec9",display:"flex",gap:8,background:"#f8f7f6"}}>
              <button onClick={()=>saveTemplate()} style={{background:C.green,color:"#fff",border:"none",borderRadius:3,padding:"6px 14px",fontSize:12,cursor:"pointer"}}> Save Current as Template</button>
              <button onClick={()=>setShowTemplateLib(false)} style={{background:"#fff",border:"1px solid #d1cec9",borderRadius:3,padding:"6px 14px",fontSize:12,cursor:"pointer"}}>Close</button>
            </div>
          </div>
        </Modal>
      )}
      {/* Map Data — column → merge-field grid, shown right after a CSV/
          Excel file is parsed (see openMapFieldsFor) and reopenable from
          the ribbon's "Match Fields" button while a file's raw rows are
          still in memory. */}
      {showMapFields&&(()=>{
        const headers=importRawRows.length?Object.keys(importRawRows[0]):[];
        const previewRows=importRawRows.slice(0,3);
        const FIELD_ROWS=[
          {key:"email",label:"Email",required:true},
          {key:"first",label:"First Name"},
          {key:"last",label:"Last Name"},
          {key:"name",label:"Full Name (fallback)"},
          {key:"company",label:"Company"},
          {key:"title",label:"Title"},
        ];
        return(
          <Modal open={showMapFields} onClose={()=>setShowMapFields(false)} width={680} ariaLabel="Map Data">
            <div style={{maxHeight:"85vh",display:"flex",flexDirection:"column",fontFamily:"'Segoe UI',sans-serif"}}>
              <div style={{background:C.nav,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                <p style={{fontSize:13,fontWeight:600,color:"#fff"}}> Map Data — {importFileName||"Recipient file"}</p>
                <button onClick={()=>setShowMapFields(false)} aria-label="Close" style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}></button>
              </div>
              <div style={{padding:"14px 16px",overflowY:"auto",flex:1}}>
                <p style={{fontSize:12,color:C.textSub,marginBottom:12,lineHeight:1.4}}>
                  {importRawRows.length} row{importRawRows.length===1?"":"s"} found, {headers.length} column{headers.length===1?"":"s"} detected. Columns were auto-matched where the name was recognizable — check each one below and fix anything that guessed wrong before importing.
                </p>
                <div style={{border:"1px solid #d1cec9",borderRadius:4,marginBottom:16}}>
                  {FIELD_ROWS.map((f,i)=>(
                    <div key={f.key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderBottom:i<FIELD_ROWS.length-1?"1px solid #edebe9":"none"}}>
                      <div style={{width:150,flexShrink:0,fontSize:12,fontWeight:600,color:C.text}}>
                        {f.label}{f.required&&<span style={{color:C.red}}> *</span>}
                      </div>
                      <span style={{fontSize:12,color:C.textMuted,flexShrink:0}}></span>
                      <select value={fieldMapping[f.key]||""} onChange={e=>setFieldMapping(p=>({...p,[f.key]:e.target.value}))}
                        style={{flex:1,border:"1px solid #d1cec9",borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,background:fieldMapping[f.key]?"#deecf9":"#fff"}}>
                        <option value="">Not mapped</option>
                        {headers.map(h=><option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                {!fieldMapping.email&&(
                  <p style={{fontSize:11,color:C.red,marginBottom:12}}>Map a column to Email before importing — recipients without an email address can't receive anything.</p>
                )}
                <p style={{fontSize:11,fontWeight:700,color:C.textSub,textTransform:"uppercase",letterSpacing:".4px",marginBottom:6}}>Preview (first {previewRows.length})</p>
                <div style={{border:"1px solid #d1cec9",borderRadius:4,overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr style={{background:"#f3f2f1"}}>
                        {["Email","First","Last","Company","Title"].map(h=>(
                          <th key={h} style={{textAlign:"left",padding:"6px 10px",borderBottom:"1px solid #d1cec9",color:C.textSub,fontWeight:700}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.length===0&&(
                        <tr><td colSpan={5} style={{padding:"10px",color:C.textMuted,textAlign:"center"}}>No rows to preview</td></tr>
                      )}
                      {previewRows.map((row,i)=>{
                        const n=normalizeRow(row,i,fieldMapping);
                        return(
                          <tr key={i} style={{borderBottom:i<previewRows.length-1?"1px solid #edebe9":"none"}}>
                            <td style={{padding:"6px 10px",color:n.email?C.text:C.red}}>{n.email||"(missing)"}</td>
                            <td style={{padding:"6px 10px",color:C.text}}>{n.first}</td>
                            <td style={{padding:"6px 10px",color:C.text}}>{n.last}</td>
                            <td style={{padding:"6px 10px",color:C.text}}>{n.company}</td>
                            <td style={{padding:"6px 10px",color:C.text}}>{n.title}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div style={{padding:"10px 16px",borderTop:"1px solid #d1cec9",display:"flex",gap:8,justifyContent:"flex-end",background:"#f8f7f6",flexShrink:0}}>
                <button onClick={()=>setShowMapFields(false)} style={{background:"#fff",border:"1px solid #d1cec9",borderRadius:3,padding:"7px 16px",fontSize:12,cursor:"pointer",color:C.text}}>Cancel</button>
                <button onClick={applyFieldMapping} disabled={!fieldMapping.email}
                  style={{background:fieldMapping.email?C.accent:"#c8c6c4",color:"#fff",border:"none",borderRadius:3,padding:"7px 16px",fontSize:12,fontWeight:600,cursor:fieldMapping.email?"pointer":"not-allowed"}}>
                  Import {importRawRows.length} recipient{importRawRows.length===1?"":"s"}
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}
      {templateCtxMenu&&(()=>{
        const {template:t}=templateCtxMenu;
        const close=()=>setTemplateCtxMenu(null);
        const items=[
          {label:"Load",fn:()=>loadTemplate(t)},
          {label:"Rename",fn:()=>startRenameTemplate(t)},
          {label:"Duplicate",fn:()=>duplicateTemplate(t)},
          {divider:true},
          {label:"Delete",fn:()=>deleteTemplate(t.id)},
        ];
        return(
          <ContextMenu open={!!templateCtxMenu} onClose={close} x={templateCtxMenu.x} y={templateCtxMenu.y} width={150}>
            {items.map((item,i)=>item.divider
              ? <ContextMenuDivider key={i}/>
              : (
                <ContextMenuItem key={i} onClick={()=>{item.fn();close();}}>
                  {item.label}
                </ContextMenuItem>
              )
            )}
          </ContextMenu>
        );
      })()}
    </div>
  );
}









































